/**
 * Main entry point for Asteroid Miner
 */

import { SolarSystem } from './rendering/SolarSystem';
import { GameState, GameStateData, FlightLogEntry } from './core/GameState';
import { AsteroidDataFetcher, AsteroidInfo, getPrimaryResourcesForType, calculateDistanceFromEarth } from './data/AsteroidData';
import staticAsteroidData from './data/asteroid-data.json';
import { formatCurrency, formatDistance, formatMass, formatDiameter } from './utils/Formatters';
import { getAsteroidWikipediaUrl, getPlanetWikipediaUrl, getSunWikipediaUrl } from './data/WikipediaLinks';
import { TickerManager, NewsType } from './ui/TickerManager';
import { MissionModalController } from './ui/MissionModalController';
import { UIManager, getUIManager } from './ui/UIManager';
import { SearchManager, BodyType } from './ui/SearchManager';
import { HelpModalController } from './ui/HelpModalController';
import { TechTreeModalController } from './ui/TechTreeModalController';
import { AssetManager, initAssetManager } from './core/AssetManager';
import { 
  TIME_SCALE_NAMES, 
  PLANET_DIAMETERS,
  MOON_DIAMETERS,
  TARGET_VISIBLE_ASTEROIDS,
  VISIBILITY_UPDATE_INTERVAL_MS,
  COPY_BUTTON_RESET_MS,
} from './utils/Constants';
import {
  LAUNCH_PROVIDERS,
  CREW_TYPES,
  Mission,
  MissionPhase,
  PHASE_INFO,
  calculatePhaseDuration,
  rollNextPhase,
  estimateResourceYield,
  DeliveryDestination,
  DESTINATION_DISPLAY_NAMES,
} from './core/MissionTypes';
import { getMissionAsteroidData, isMissionSuccessRewardPending } from './core/MissionRewards';
import { PROGRESSION_LEVELS, getCurrentLevel } from './data/HelpContent';
import { parseUrlParams, copySaveUrl, isDevMode } from './utils/SaveSystem';
import { 
  ResourceType, 
  RESOURCE_BASE_PRICES,
  UI_COLORS,
} from './utils/Constants';
import {
  RESOURCE_INFO,
  updateMarketPrices,
  getSpotPrice,
  getPriceTrend,
  formatPrice,
  getResourcesByPrice,
} from './data/MarketSystem';
import {
  OwnableAsset,
  AssetCategory,
  CATEGORY_INFO as ASSET_CATEGORY_INFO,
  ALL_OWNABLE_ASSETS,
  getAssetsByCategory,
  getAssetById,
} from './data/OwnableAssets';
import {
  StorageDepot,
  STORAGE_DEPOTS,
  getDepotById,
  getDepotUsedCapacity,
  formatCapacity,
  getInstanceTotalCapacity,
  getTotalStorageCapacityNew,
  getTotalStoredTonsNew,
  createEmptyStorage,
} from './data/StorageDepots';
import {
  checkPirateAttack,
  resolveCombat,
  formatCombatNews,
  CombatResult,
} from './data/PirateSystem';
import {
  NewsSystem,
  getNewsSystem,
  EasterEggState,
} from './data/NewsSystem';

// Alias for RESOURCE_INFO from MarketSystem
const MARKET_RESOURCE_INFO = RESOURCE_INFO;

class Game {
  private solarSystem: SolarSystem | null = null;
  private gameState: GameState;
  private lastTime = 0;
  private isRunning = false;
  private asteroidData: Map<string, AsteroidInfo> = new Map();
  private debugMode = false;
  private lastKnownLevel = 1; // Track level for transition detection
  
  // News system
  private newsSystem: NewsSystem;
  private lastNewsCheck = 0; // Game time of last news check
  private lastEasterEggState: { month: number; day: number; missions: number; balanceTier: string } | null = null;
  
  // UI managers (extracted for better organization)
  private tickerManager: TickerManager;
  private missionModal: MissionModalController | null = null;
  private ui: UIManager;
  private searchManager: SearchManager | null = null;
  private helpModal: HelpModalController | null = null;
  private techTreeModal: TechTreeModalController | null = null;
  
  // Asset manager for owned vehicles
  private assetManager: AssetManager;
  
  // Dynamic asteroid visibility
  private lastVisibilityUpdate = 0;

  constructor() {
    this.gameState = GameState.getInstance();
    this.lastKnownLevel = getCurrentLevel(this.gameState.data.missionsCompleted).id;
    this.newsSystem = getNewsSystem();
    this.tickerManager = new TickerManager();
    this.ui = getUIManager();
    this.assetManager = initAssetManager();
    this.init();
  }

  private async init(): Promise<void> {
    console.log('Initializing Asteroid Miner...');
    
    // Initialize UI manager to cache DOM elements
    this.ui.init();

    // Load state from URL params if present
    const urlState = parseUrlParams();
    if (urlState) {
      this.gameState.loadState(urlState);
      if (isDevMode() && new URLSearchParams(window.location.search).get('debug') === '1') {
        this.debugMode = true;
        console.log('DEBUG MODE ACTIVE - State loaded from URL params');
      }
    }

    // Get canvas from UIManager
    const canvas = this.ui.canvas;
    if (!canvas) {
      console.error('Canvas not found!');
      return;
    }

    // Prevent right-click context menu on canvas
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());

    // Initialize solar system renderer
    this.solarSystem = new SolarSystem(canvas);

    // Set up UI event listeners
    this.setupUI();

    // Load asteroids
    await this.loadAsteroids();

    // Restore mined asteroid state (hide labels for already-mined asteroids)
    this.gameState.data.minedAsteroids.forEach(asteroidId => {
      if (this.solarSystem) {
        this.solarSystem.setAsteroidMined(asteroidId, true);
      }
    });

    // Initialize search manager
    this.searchManager = new SearchManager(this.ui, {
      onAddNewsItem: (text, type) => this.addNewsItem(text, type),
      onAsteroidAdded: (asteroid) => this.addAsteroidToScene(asteroid),
      onSelectAndFocus: (name, type, id) => this.selectAndFocusBody(name, type, id),
      getAsteroidData: () => this.asteroidData,
      getStaticAsteroidData: () => staticAsteroidData as AsteroidInfo[],
    });
    this.searchManager.init(staticAsteroidData as AsteroidInfo[]);

    // Show ready button, hide progress
    this.ui.hide(this.ui.loadingStatus);
    const progressBar = document.querySelector('.loading-progress') as HTMLElement;
    if (progressBar) progressBar.style.display = 'none';
    this.ui.show(this.ui.launchBtn);

    // Wait for user to click "Ready to Launch"
    const loading = this.ui.loading;
    await new Promise<void>(resolve => {
      const launchBtn = this.ui.launchBtn;
      if (launchBtn) {
        launchBtn.addEventListener('click', () => {
          if (loading) loading.classList.add('hidden');
          resolve();
        });
      }
    });

    // Start game loop
    this.isRunning = true;
    this.lastTime = performance.now();
    requestAnimationFrame(this.gameLoop.bind(this));

    // Initialize random starting news (after Welcome message)
    this.initializeStartingNews();

    // Show debug indicator if in debug mode
    if (this.debugMode) {
      this.showDebugIndicator();
    }

    // Setup save URL button
    this.setupSaveButton();
    
    // Setup ticker scroll and pause on hover
    this.tickerManager.setup();

    console.log('Game initialized!');
  }

  /**
   * Add random starting news items so each game feels fresh
   */
  private initializeStartingNews(): void {
    // Add 2-3 random flavor/educational items
    const flavorNews = this.newsSystem.getFlavorNews();
    if (flavorNews) {
      this.addNewsItem(flavorNews, 'flavor');
    }
    
    const eduNews = this.newsSystem.getEducationalNews();
    if (eduNews) {
      this.addNewsItem(eduNews, 'flavor'); // Educational uses flavor style (gray)
    }
    
    // Maybe add another flavor item
    if (Math.random() > 0.5) {
      const moreFlavorNews = this.newsSystem.getFlavorNews();
      if (moreFlavorNews) {
        this.addNewsItem(moreFlavorNews, 'flavor');
      }
    }
  }

  private showDebugIndicator(): void {
    const indicator = document.createElement('div');
    indicator.id = 'debug-indicator';
    indicator.innerHTML = '🔧 DEBUG MODE';
    indicator.style.cssText = `
      position: fixed;
      top: 10px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(255, 100, 0, 0.9);
      color: white;
      padding: 4px 12px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: bold;
      z-index: 9999;
    `;
    document.body.appendChild(indicator);
  }

  private setupSaveButton(): void {
    const saveBtn = document.getElementById('save-url-btn');
    if (saveBtn) {
      saveBtn.addEventListener('click', async () => {
        const success = await copySaveUrl();
        if (success) {
          this.addNewsItem('Save URL copied to clipboard!', 'important');
          saveBtn.textContent = '✓ Copied!';
          setTimeout(() => {
            saveBtn.textContent = 'Copy Save URL';
          }, COPY_BUTTON_RESET_MS);
        } else {
          this.addNewsItem('Failed to copy save URL', 'critical');
        }
      });
    }
  }

  private async loadAsteroids(): Promise<void> {
    console.log('Loading asteroid data...');

    const asteroidList = AsteroidDataFetcher.getDefaultAsteroids();
    const progressBar = document.getElementById('loading-progress-bar');
    const statusText = document.getElementById('loading-status');

    try {
      const asteroids = await AsteroidDataFetcher.loadAsteroids(asteroidList, (current, total) => {
        const percent = Math.round((current / total) * 100);
        if (progressBar) progressBar.style.width = `${percent}%`;
        if (statusText) statusText.textContent = `Loading asteroids... ${current}/${total}`;
      });

      console.log(`Loaded ${asteroids.length} asteroids`);

      // Add asteroids to scene
      asteroids.forEach((asteroid) => {
        this.asteroidData.set(asteroid.id, asteroid);

        // Estimate size for rendering (smaller than real size for visibility)
        const size = asteroid.diameter ? Math.max(0.5, asteroid.diameter / 20) : 1;

        // Determine type
        const type = (asteroid.taxonomicClass as 'C' | 'S' | 'M') || 'S';

        this.solarSystem?.addAsteroid(asteroid.id, asteroid.name, asteroid.semiMajorAxis, size, type);
      });

      // Update UI
      this.updateAsteroidCount();
    } catch (error) {
      console.error('Failed to load asteroids:', error);

      // Fallback: add some mock asteroids
      console.log('Using mock asteroid data');
      for (let i = 0; i < 20; i++) {
        const id = `mock-${i}`;
        const name = `Asteroid ${i}`;
        const asteroid = AsteroidDataFetcher.generateMockAsteroid(id, name);
        this.asteroidData.set(id, asteroid);

        const size = asteroid.diameter ? Math.max(0.5, asteroid.diameter / 20) : 1;
        const type = asteroid.taxonomicClass as 'C' | 'S' | 'M';

        this.solarSystem?.addAsteroid(id, name, asteroid.semiMajorAxis, size, type);
      }

      this.updateAsteroidCount();
    }
  }

  private setupUI(): void {
    // Subscribe to game state changes
    this.gameState.subscribe('ui', (data) => {
      // Update balance
      this.ui.setText(this.ui.balance, formatCurrency(data.balance));

      // Update reputation
      this.ui.setText(this.ui.reputation, data.reputation.toString());

      // Update missions completed
      this.ui.setText(this.ui.missionsCompleted, data.missionsCompleted.toString());

      // Update current level
      if (this.ui.currentLevel) {
        const currentLevel = getCurrentLevel(data.missionsCompleted);
        this.ui.currentLevel.innerHTML = `${currentLevel.id}/5: ${currentLevel.name}`;
      }

      // Check ULA unlock
      if (this.gameState.checkULAUnlock()) {
        this.addNewsItem(
          `BREAKING: ULA will now accept contracts from ${data.playerName}`,
          'important'
        );
      }
    });

    // Object selection
    window.addEventListener('objectSelected', ((event: CustomEvent) => {
      this.onObjectSelected(event.detail);
    }) as EventListener);

    // Object hover
    window.addEventListener('objectHovered', ((event: CustomEvent) => {
      this.onObjectHovered(event.detail);
    }) as EventListener);

    // Copy save button
    const copyBtn = this.ui.copySaveBtn;
    if (copyBtn) {
      copyBtn.addEventListener('click', () => {
        const input = this.ui.saveUrlInput;
        if (input) {
          input.select();
          document.execCommand('copy');
          copyBtn.textContent = 'Copied!';
          setTimeout(() => {
            copyBtn.textContent = 'Copy Save';
          }, COPY_BUTTON_RESET_MS);
        }
      });
    }

    // Plan mission button
    const planBtn = document.getElementById('plan-mission-btn');
    if (planBtn) {
      planBtn.addEventListener('click', () => {
        this.onPlanMission();
      });
    }

    // Time slider
    const timeSlider = document.getElementById('time-slider') as HTMLInputElement;
    if (timeSlider) {
      timeSlider.addEventListener('input', (e) => {
        const index = parseInt((e.target as HTMLInputElement).value);
        this.gameState.setTimeScaleByIndex(index);
        this.updateTimeDisplay(index);
      });
      this.updateTimeDisplay(4); // Initialize display to 1 Day/Sec (default)
    }

    // Breadcrumb link
    const breadcrumbLink = document.getElementById('breadcrumb-link');
    if (breadcrumbLink) {
      breadcrumbLink.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.solarSystem) {
          this.solarSystem.focusOnObject(null);
        }
      });
    }

    // Camera focus events
    window.addEventListener('cameraFocusChanged', ((event: CustomEvent) => {
      this.onCameraFocusChanged(event.detail);
    }) as EventListener);

    // Search box setup
    this.searchManager?.setup();

    // Mission modal setup
    this.setupMissionModal();

    // Phase modal setup
    this.setupPhaseModal();

    // Mission list click handler (event delegation)
    this.setupMissionListClickHandler();

    // Help system setup
    this.helpModal = new HelpModalController(this.gameState, this.ui);
    this.helpModal.setup();

    // Tech tree setup
    this.techTreeModal = new TechTreeModalController(this.gameState, this.ui, {
      onAddNewsItem: (text, type) => this.addNewsItem(text, type),
    });
    this.techTreeModal.setup();

    // Assets system setup
    this.setupAssets();
    
    // Sync AssetManager from gameState's ownedAssets (legacy migration)
    this.assetManager.syncFromLegacyAssets(this.gameState.data.ownedAssets);
    // Also rebuild in-use tracking from active missions
    this.assetManager.syncWithActiveMissions(this.gameState.data.activeMissions);

    // Storage system setup
    this.setupStorage();

    // Level transition setup
    this.setupLevelTransitions();

    // Market display setup
    this.setupMarket();
  }

  private onObjectSelected(userData: any): void {
    if (!userData) {
      // Deselected
      this.ui.setHTML(this.ui.targetInfo, '<p style="color: #888;">Select an asteroid to view details</p>');

      this.gameState.selectAsteroid(null);
      return;
    }

    if (userData.type === 'asteroid') {
      const asteroid = this.asteroidData.get(userData.id);
      if (asteroid) {
        this.displayAsteroidInfo(asteroid);
        this.gameState.selectAsteroid(asteroid.id);
      }
    } else if (userData.type === 'planet') {
      this.displayPlanetInfo(userData);
    } else if (userData.type === 'sun') {
      this.displaySunInfo(userData);
      // Easter egg: Allow selecting Sun for mission planning
      this.gameState.selectAsteroid('sun');
    } else if (userData.type === 'moon' || userData.type === 'satellite') {
      this.displayMoonInfo(userData);
    }
  }

  private displayAsteroidInfo(asteroid: AsteroidInfo): void {
    const targetInfo = this.ui.targetInfo;
    if (!targetInfo) return;

    const diameterStr = asteroid.diameter ? formatDiameter(asteroid.diameter) : 'Unknown';
    const massStr = asteroid.mass ? formatMass(asteroid.mass) : 'Unknown';

    const wikiUrl = getAsteroidWikipediaUrl(asteroid.id, asteroid.name);
    const jplUrl = `https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=${encodeURIComponent(asteroid.id)}`;

    targetInfo.innerHTML = `
      <p><strong style="color: #0ff; font-size: 14px;">${asteroid.name}</strong></p>
      <p style="margin-top: 10px;"><span class="label">Type:</span> ${asteroid.taxonomicClass}-class</p>
      <p><span class="label">Distance:</span> ${formatDistance(asteroid.semiMajorAxis)}</p>
      <p><span class="label">Diameter:</span> ${diameterStr}</p>
      <p><span class="label">Est. Mass:</span> ${massStr}</p>
      <p><span class="label">Eccentricity:</span> ${asteroid.eccentricity.toFixed(3)}</p>
      <p><span class="label">Inclination:</span> ${asteroid.inclination.toFixed(1)}°</p>
      <p style="margin-top: 10px;">
        <a href="${wikiUrl}" target="_blank" style="color: #0ff;">Wikipedia ↗</a>
        &nbsp;|&nbsp;
        <a href="${jplUrl}" target="_blank" style="color: #0ff;">JPL SBDB ↗</a>
      </p>
    `;
  }

  private displayPlanetInfo(userData: any): void {
    const targetInfo = this.ui.targetInfo;
    if (!targetInfo) return;

    const wikiUrl = getPlanetWikipediaUrl(userData.name);
    const jplUrl = 'https://ssd.jpl.nasa.gov/planets/phys_par.html';
    const diameter = PLANET_DIAMETERS[userData.name];
    const diameterStr = diameter ? formatDiameter(diameter) : 'Unknown';

    targetInfo.innerHTML = `
      <p><strong style="color: #0ff; font-size: 14px;">${userData.name}</strong></p>
      <p style="margin-top: 10px;"><span class="label">Type:</span> Planet</p>
      <p><span class="label">Distance:</span> ${formatDistance(userData.orbit)}</p>
      <p><span class="label">Diameter:</span> ${diameterStr}</p>
      <p style="margin-top: 10px; color: #888;">Planets cannot be mined (yet)</p>
      <p style="margin-top: 10px;">
        <a href="${wikiUrl}" target="_blank" style="color: #0ff;">Wikipedia ↗</a>
        &nbsp;|&nbsp;
        <a href="${jplUrl}" target="_blank" style="color: #0ff;">JPL Planets ↗</a>
      </p>
    `;
  }

  private displaySunInfo(_userData: any): void {
    const targetInfo = this.ui.targetInfo;
    if (!targetInfo) return;

    const wikiUrl = getSunWikipediaUrl();
    const jplUrl = 'https://ssd.jpl.nasa.gov/planets/phys_par.html';
    const diameterStr = formatDiameter(PLANET_DIAMETERS.Sun);

    targetInfo.innerHTML = `
      <p><strong style="color: #ff0; font-size: 14px;">Sun</strong></p>
      <p style="margin-top: 10px;"><span class="label">Type:</span> G-type Main Sequence Star</p>
      <p><span class="label">Distance:</span> 0.00 AU</p>
      <p><span class="label">Diameter:</span> ${diameterStr}</p>
      <p><span class="label">Mass:</span> 1.989 × 10³⁰ kg</p>
      <p style="margin-top: 10px; color: #888;">The center of our solar system</p>
      <p style="margin-top: 10px;">
        <a href="${wikiUrl}" target="_blank" style="color: #0ff;">Wikipedia ↗</a>
        &nbsp;|&nbsp;
        <a href="${jplUrl}" target="_blank" style="color: #0ff;">JPL Planets ↗</a>
      </p>
    `;
  }

  private displayMoonInfo(userData: any): void {
    const targetInfo = this.ui.targetInfo;
    if (!targetInfo) return;

    const wikiUrl = getAsteroidWikipediaUrl('', userData.name); // Moons use simple name lookup

    const typeStr = userData.type === 'satellite' ? 'Space Station' : 'Moon';
    const parentStr = userData.parent || 'Unknown';
    const diameter = MOON_DIAMETERS[userData.name];
    const diameterStr = diameter ? formatDiameter(diameter) : null;

    targetInfo.innerHTML = `
      <p><strong style="color: #0ff; font-size: 14px;">${userData.name}</strong></p>
      <p style="margin-top: 10px;"><span class="label">Type:</span> ${typeStr}</p>
      <p><span class="label">Orbits:</span> ${parentStr}</p>
      ${diameterStr ? `<p><span class="label">Diameter:</span> ${diameterStr}</p>` : ''}
      ${userData.orbitalPeriod ? `<p><span class="label">Period:</span> ${userData.orbitalPeriod.toFixed(2)} days</p>` : ''}
      <p style="margin-top: 10px; color: #888;">${userData.type === 'satellite' ? 'Human-made orbital station' : 'Natural satellite'}</p>
      <p style="margin-top: 10px;"><a href="${wikiUrl}" target="_blank" style="color: #0ff;">Wikipedia ↗</a></p>
    `;
  }

  private onObjectHovered(userData: any): void {
    const hoverInfo = document.getElementById('hover-info');
    if (!hoverInfo) return;

    if (!userData) {
      hoverInfo.innerHTML = '<p style="color: #888;">Hover over objects to see details</p>';
      return;
    }

    if (userData.type === 'sun') {
      const wikiUrl = getSunWikipediaUrl();
      const jplUrl = 'https://ssd.jpl.nasa.gov/planets/phys_par.html';
      const diameterStr = formatDiameter(PLANET_DIAMETERS.Sun);
      hoverInfo.innerHTML = `
        <p><strong style="color: #ff0;">Sun</strong></p>
        <p><span class="label">Type:</span> G-type Star</p>
        <p><span class="label">Diameter:</span> ${diameterStr}</p>
        <p><span class="label">Distance:</span> 0.00 AU</p>
        <p><span class="label">Visited:</span> <span style="color: #f00;">No</span></p>
        <p style="margin-top: 5px;"><a href="${wikiUrl}" target="_blank" style="color: #0ff; font-size: 11px;">Wiki ↗</a> | <a href="${jplUrl}" target="_blank" style="color: #0ff; font-size: 11px;">JPL ↗</a></p>
      `;
    } else if (userData.type === 'planet') {
      // Calculate distance from Earth (simplified - assuming Earth is at 1 AU)
      const distanceFromEarth = Math.abs(userData.orbit - 1.0);
      const wikiUrl = getPlanetWikipediaUrl(userData.name);
      const jplUrl = 'https://ssd.jpl.nasa.gov/planets/phys_par.html';
      const diameter = PLANET_DIAMETERS[userData.name];
      const diameterStr = diameter ? formatDiameter(diameter) : 'Unknown';
      hoverInfo.innerHTML = `
        <p><strong style="color: #0ff;">${userData.name}</strong></p>
        <p><span class="label">Type:</span> Planet</p>
        <p><span class="label">Diameter:</span> ${diameterStr}</p>
        <p><span class="label">From Earth:</span> ${formatDistance(distanceFromEarth)}</p>
        <p><span class="label">Visited:</span> <span style="color: #f00;">No</span></p>
        <p style="margin-top: 5px;"><a href="${wikiUrl}" target="_blank" style="color: #0ff; font-size: 11px;">Wiki ↗</a> | <a href="${jplUrl}" target="_blank" style="color: #0ff; font-size: 11px;">JPL ↗</a></p>
      `;
    } else if (userData.type === 'asteroid') {
      const asteroid = this.asteroidData.get(userData.id);
      if (!asteroid) return;

      // Get rendered positions for accurate distance
      const asteroidAngle = this.solarSystem?.getAsteroidAngle(userData.id);
      const earthAngle = this.solarSystem?.getEarthAngle();
      const distanceInfo = calculateDistanceFromEarth(asteroid, this.gameState.data.gameTime, asteroidAngle, earthAngle);
      const distanceStr = `${formatDistance(distanceInfo.current)} (${distanceInfo.isNear ? 'near' : 'far'})`;
      const diameterStr = asteroid.diameter ? formatDiameter(asteroid.diameter) : 'Unknown';
      const massStr = asteroid.mass ? formatMass(asteroid.mass) : 'Unknown';
      const wikiUrl = getAsteroidWikipediaUrl(asteroid.id, asteroid.name);
      const jplUrl = `https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=${encodeURIComponent(asteroid.id)}`;
      
      // Check if asteroid has been mined
      const isMined = this.gameState.data.minedAsteroids.includes(userData.id);
      const visitedHtml = isMined 
        ? '<span style="color: #4ade80;">✓ Mined</span>'
        : '<span style="color: #888;">Not yet</span>';

      hoverInfo.innerHTML = `
        <p><strong style="color: #0ff;">${asteroid.name}</strong></p>
        <p><span class="label">Type:</span> ${asteroid.taxonomicClass}-class</p>
        <p><span class="label">Diameter:</span> ${diameterStr}</p>
        <p><span class="label">Mass:</span> ${massStr}</p>
        <p><span class="label">From Earth:</span> ${distanceStr}</p>
        <p><span class="label">Status:</span> ${visitedHtml}</p>
        <p style="margin-top: 5px;">
          <a href="${wikiUrl}" target="_blank" style="color: #0ff; font-size: 11px;">Wiki ↗</a>
          &nbsp;|&nbsp;
          <a href="${jplUrl}" target="_blank" style="color: #0ff; font-size: 11px;">JPL ↗</a>
        </p>
      `;
    }
  }

  /**
   * Get distance info for an asteroid using rendered positions
   */
  private getAsteroidDistanceInfo(asteroid: AsteroidInfo): { current: number; min: number; max: number; isNear: boolean } {
    const asteroidAngle = this.solarSystem?.getAsteroidAngle(asteroid.id);
    const earthAngle = this.solarSystem?.getEarthAngle();
    return calculateDistanceFromEarth(asteroid, this.gameState.data.gameTime, asteroidAngle, earthAngle);
  }

  private onPlanMission(): void {
    this.missionModal?.startPlanning();
  }

  /** Add an asteroid to the scene (called by SearchManager) */
  private addAsteroidToScene(asteroid: AsteroidInfo): void {
    // Add to our data store
    this.asteroidData.set(asteroid.id, asteroid);
    
    // Add to solar system scene
    if (this.solarSystem) {
      const size = asteroid.diameter ? Math.max(0.5, asteroid.diameter / 20) : 1;
      const asteroidType = (asteroid.taxonomicClass as 'C' | 'S' | 'M') || 'S';
      this.solarSystem.addAsteroid(asteroid.id, asteroid.name, asteroid.semiMajorAxis, size, asteroidType);
    }
    
    // Update asteroid count display
    this.updateAsteroidCount();
  }
  
  /** Select and focus on a body (called by SearchManager) */
  private selectAndFocusBody(name: string, type: BodyType, id?: string): void {
    if (this.solarSystem) {
      this.solarSystem.selectAndFocusByName(name, type, id);
    }
  }

  private setupMissionModal(): void {
    // Create mission modal controller with callbacks
    this.missionModal = new MissionModalController(
      this.gameState,
      this.asteroidData,
      {
        onAddNewsItem: (text, type) => this.addNewsItem(text, type),
        onUpdateActiveMissionsList: () => this.updateActiveMissionsList(),
        getAsteroidDistanceInfo: (asteroid) => this.getAsteroidDistanceInfo(asteroid),
        isAsteroidAvailable: (id) => this.isAsteroidAvailable(id),
        getSolarSystemAngles: (asteroidId) => ({
          asteroidAngle: this.solarSystem?.getAsteroidAngle(asteroidId),
          earthAngle: this.solarSystem?.getEarthAngle(),
        }),
        onRefreshAsteroids: () => this.loadAsteroids(),
        getAllAsteroids: () => staticAsteroidData as AsteroidInfo[],
        getAssetManager: () => this.assetManager,
      }
    );
    this.missionModal.setup();
  }

  private getMissionAsteroid(mission: Pick<Mission, 'asteroidId'>): AsteroidInfo | undefined {
    return getMissionAsteroidData(
      mission,
      this.asteroidData,
      staticAsteroidData as AsteroidInfo[]
    );
  }

  private isResourceType(value: string | undefined): value is ResourceType {
    return !!value && Object.values(ResourceType).includes(value as ResourceType);
  }

  private getMissionCrewEfficiency(mission: Mission): number {
    const crew = CREW_TYPES.find(c => c.id === mission.crewId);
    if (crew) {
      return crew.miningEfficiency;
    }

    const ownedCrew = mission.ownedCrewId ? getAssetById(mission.ownedCrewId) : undefined;
    return ownedCrew?.category === 'crew' && ownedCrew.efficiency
      ? ownedCrew.efficiency
      : 1;
  }

  private updateActiveMissionsList(): void {
    const container = this.ui.activeMissionsList;
    if (!container) return;

    const missions = this.gameState.data.activeMissions;
    
    if (missions.length === 0) {
      container.innerHTML = '<p style="color: #888;">No active missions</p>';
      return;
    }

    container.innerHTML = missions.map(mission => {
      const progressPercent = Math.round(mission.progress * 100);
      const phaseInfo = PHASE_INFO[mission.currentPhase];
      
      // Determine terminal state class
      let terminalClass = '';
      if (phaseInfo.isTerminal) {
        terminalClass = phaseInfo.isSuccess ? 'terminal-success' : 'terminal-failure';
      }
      
      // Pulse class for phase changes
      const pulseClass = mission.phaseJustChanged ? 'pulse' : '';
      
      return `
        <div class="mission-item ${terminalClass}" data-mission-id="${mission.id}">
          <div class="mission-name">${mission.asteroidName}</div>
          <div class="mission-status">${this.getMissionStatusText(mission)}</div>
          <div class="mission-progress ${pulseClass}">
            <div class="mission-progress-bar" style="width: ${progressPercent}%"></div>
          </div>
        </div>
      `;
    }).join('');

    // Clear phaseJustChanged flags after rendering
    missions.forEach(mission => {
      if (mission.phaseJustChanged) {
        this.gameState.updateMission(mission.id, { phaseJustChanged: false });
      }
    });
  }

  private setupMissionListClickHandler(): void {
    const container = document.getElementById('active-missions-list');
    if (!container) return;

    // Use event delegation - attach once to container
    container.addEventListener('click', (e) => {
      const missionItem = (e.target as HTMLElement).closest('.mission-item');
      if (missionItem) {
        const missionId = (missionItem as HTMLElement).dataset.missionId;
        if (missionId) {
          const mission = this.gameState.getMission(missionId);
          if (mission && isMissionSuccessRewardPending(mission)) {
            const processed = this.onMissionComplete(mission);
            if (processed) {
              this.updateActiveMissionsList();
            }
          }
          this.showPhaseModal(missionId);
        }
      }
    });
  }

  private getMissionStatusText(mission: Mission): string {
    const phaseInfo = PHASE_INFO[mission.currentPhase];
    
    if (phaseInfo.isTerminal) {
      if (mission.currentPhase === MissionPhase.MISSION_SUCCESS) {
        return mission.completionProcessed
          ? `${phaseInfo.name} - Reward collected`
          : `${phaseInfo.name} - Click to collect`;
      }
      return phaseInfo.name;
    }
    
    const phaseElapsed = this.gameState.data.gameTime - mission.phaseStartTime;
    const phaseRemaining = Math.max(0, Math.round(mission.phaseDuration - phaseElapsed));
    
    return `${phaseInfo.name} - ${phaseRemaining} days`;
  }

  private showPhaseModal(missionId: string): void {
    const mission = this.gameState.getMission(missionId);
    if (!mission) return;

    const phaseInfo = PHASE_INFO[mission.currentPhase];
    const modal = document.getElementById('phase-modal');
    const content = document.getElementById('phase-modal-content');
    const image = document.getElementById('phase-modal-image') as HTMLImageElement;
    const title = document.getElementById('phase-modal-title');
    const asteroidInfo = document.getElementById('phase-modal-asteroid-info');
    const description = document.getElementById('phase-modal-description');
    const details = document.getElementById('phase-modal-details');

    if (!modal || !content || !image || !title || !asteroidInfo || !description || !details) return;

    // Get asteroid data for display
    const asteroid = this.getMissionAsteroid(mission);
    const AU_IN_KM = 149597870.7;
    const SPEED_OF_LIGHT = 299792.458; // km/s

    // Set basic content
    image.src = phaseInfo.image;
    image.alt = phaseInfo.name;
    title.textContent = phaseInfo.name;
    description.textContent = phaseInfo.description;

    // Build asteroid info section with Wikipedia and JPL links
    const wikipediaUrl = `https://en.wikipedia.org/w/index.php?search=${encodeURIComponent(mission.asteroidName)}`;
    const jplUrl = `https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=${encodeURIComponent(mission.asteroidId)}`;
    const distanceKm = mission.distanceAU ? (mission.distanceAU * AU_IN_KM).toFixed(0) : 'N/A';
    const diameterDisplay = asteroid?.diameter ? `${asteroid.diameter.toFixed(1)} km` : 'Unknown';
    const typeDisplay = asteroid?.taxonomicClass || 'Unknown';
    
    asteroidInfo.innerHTML = `
      <strong>Target:</strong> <a href="${wikipediaUrl}" target="_blank" rel="noopener">${mission.asteroidName}</a>
      (<a href="${jplUrl}" target="_blank" rel="noopener" style="font-size: 11px;">JPL</a>)<br>
      <strong>Type:</strong> ${typeDisplay}-type &nbsp;|&nbsp; 
      <strong>Diameter:</strong> ${diameterDisplay} &nbsp;|&nbsp;
      <strong>Distance:</strong> ${mission.distanceAU?.toFixed(3) || 'N/A'} AU (${Number(distanceKm).toLocaleString()} km)
    `;

    // Build phase-specific details
    let detailsHtml = '';
    
    switch (mission.currentPhase) {
      case MissionPhase.CONTRACT_SIGNED: {
        // Show payload type, qty, due date, agreed price, market price
        // Game starts January 1, 2032 - calculate due date from launchTime + deadline
        const gameStartDate = new Date(2032, 0, 1); // January 1, 2032
        const deadlineDays = mission.contractDeadline || mission.totalDuration;
        const dueDateGameDays = mission.launchTime + deadlineDays;
        const dueDate = new Date(gameStartDate.getTime() + dueDateGameDays * 24 * 60 * 60 * 1000);
        const dueDateStr = dueDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        const currentPrice = getSpotPrice(this.gameState.data.market, mission.resourceType as ResourceType);
        const expectedPayload = mission.expectedPayload || 0;
        
        // Show warning if mission duration exceeds deadline
        const missionDays = mission.totalDuration;
        const isAchievable = missionDays <= deadlineDays;
        const daysToSpare = deadlineDays - missionDays;
        
        // Calculate contract price per ton and premium
        const contractPricePerTon = mission.contractValue && expectedPayload > 0 
          ? mission.contractValue / expectedPayload 
          : null;
        const premiumPercent = contractPricePerTon && currentPrice > 0
          ? ((contractPricePerTon - currentPrice) / currentPrice) * 100
          : null;
        
        detailsHtml = `
          <div class="detail-section">
            <div><span class="detail-label">Payload Type:</span> <span class="detail-value highlight">${mission.resourceType}</span></div>
            <div><span class="detail-label">Expected Yield:</span> <span class="detail-value">${expectedPayload.toFixed(1)} tons</span></div>
            <div><span class="detail-label">Due Date:</span> <span class="detail-value">${dueDateStr}</span> <span style="color: var(--text-muted);">(${deadlineDays} days)</span></div>
            <div><span class="detail-label">Est. Mission Time:</span> <span class="detail-value ${isAchievable ? '' : 'loss'}">${missionDays.toFixed(1)} days</span> ${isAchievable ? `<span class="detail-value profit">(${daysToSpare.toFixed(1)} days to spare)</span>` : '<span class="detail-value loss">(WILL BE LATE)</span>'}</div>
            ${mission.contractValue ? `<div><span class="detail-label">Contract Value:</span> <span class="detail-value profit">$${mission.contractValue.toLocaleString()}</span></div>` : ''}
            ${mission.contractVendorName ? `<div><span class="detail-label">Vendor:</span> <span class="detail-value">${mission.contractVendorName}</span></div>` : ''}
            ${contractPricePerTon ? `<div><span class="detail-label">Agreed Price:</span> <span class="detail-value">$${contractPricePerTon.toLocaleString(undefined, {maximumFractionDigits: 0})}/ton</span> ${premiumPercent !== null ? `<span class="detail-value ${premiumPercent >= 0 ? 'profit' : 'loss'}">(${premiumPercent >= 0 ? '+' : ''}${premiumPercent.toFixed(1)}% vs market)</span>` : ''}</div>` : ''}
            <div><span class="detail-label">Current Market Price:</span> <span class="detail-value">$${currentPrice.toLocaleString()}/ton</span></div>
          </div>
        `;
        break;
      }
      
      case MissionPhase.LAUNCH: {
        // Show provider, payload weight, launch date, wet dress rehearsal
        const launchDate = new Date(mission.phaseStartTime);
        const launchDateStr = launchDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const payloadWeight = mission.expectedPayload ? (mission.expectedPayload * 1000).toFixed(0) : 'N/A'; // Convert tons to kg
        
        detailsHtml = `
          <div class="detail-section">
            <div><span class="detail-label">Launch Provider:</span> <span class="detail-value highlight">${mission.providerName}</span></div>
            <div><span class="detail-label">Payload to Orbit:</span> <span class="detail-value">${Number(payloadWeight).toLocaleString()} kg</span></div>
            <div><span class="detail-label">Launch Date:</span> <span class="detail-value">${launchDateStr}</span></div>
            <div><span class="detail-label">Wet Dress Rehearsal:</span> <span class="detail-value profit">✓ PASSED</span></div>
            <div><span class="detail-label">Provider Reliability:</span> <span class="detail-value">${((mission.providerReliability || 0.98) * 100).toFixed(0)}%</span></div>
          </div>
        `;
        break;
      }
      
      case MissionPhase.OUTBOUND: {
        // Show vehicle speed, distance in AU and km, captain's name
        const distanceKmNum = mission.distanceAU ? mission.distanceAU * AU_IN_KM : 0;
        const travelTimeSeconds = (mission.outboundDuration || 30) * 24 * 60 * 60;
        const speedKmS = distanceKmNum / travelTimeSeconds;
        const speedPercentC = (speedKmS / SPEED_OF_LIGHT) * 100;
        
        detailsHtml = `
          <div class="detail-section">
            <div><span class="detail-label">Captain:</span> <span class="detail-value highlight">${mission.captainName || 'Unknown'}</span></div>
            <div><span class="detail-label">Distance:</span> <span class="detail-value">${mission.distanceAU?.toFixed(3) || 'N/A'} AU (${distanceKmNum.toLocaleString(undefined, {maximumFractionDigits: 0})} km)</span></div>
            <div><span class="detail-label">Vehicle Speed:</span> <span class="detail-value">${speedKmS.toFixed(1)} km/s (${speedPercentC.toFixed(4)}% c)</span></div>
            <div><span class="detail-label">Travel Time:</span> <span class="detail-value">${mission.outboundDuration || 30} days</span></div>
            <div><span class="detail-label">Crew:</span> <span class="detail-value">${mission.crewName}</span></div>
          </div>
        `;
        break;
      }
      
      case MissionPhase.DRILLING: {
        // Show crew type, current payload extracted (based on time elapsed)
        const phaseElapsed = (this.gameState.data.gameTime - mission.phaseStartTime) / (24 * 60 * 60 * 1000);
        const phaseDuration = mission.miningDuration || 30;
        const progress = Math.min(phaseElapsed / phaseDuration, 1);
        const currentExtracted = (mission.expectedPayload || 0) * progress;
        
        detailsHtml = `
          <div class="detail-section">
            <div><span class="detail-label">Crew Type:</span> <span class="detail-value highlight">${mission.crewName}</span></div>
            <div><span class="detail-label">Mining Progress:</span> <span class="detail-value">${(progress * 100).toFixed(1)}%</span></div>
            <div><span class="detail-label">Payload Extracted:</span> <span class="detail-value">${currentExtracted.toFixed(2)} tons of ${mission.resourceType}</span></div>
            <div><span class="detail-label">Target Payload:</span> <span class="detail-value">${(mission.expectedPayload || 0).toFixed(1)} tons</span></div>
            <div><span class="detail-label">Crew Reliability:</span> <span class="detail-value">${((mission.crewReliability || 0.98) * 100).toFixed(0)}%</span></div>
          </div>
        `;
        break;
      }
      
      case MissionPhase.INBOUND: {
        // Show vehicle speed, distance, captain, payload type/weight/%
        const distanceKmNum = mission.distanceAU ? mission.distanceAU * AU_IN_KM : 0;
        const travelTimeSeconds = (mission.returnDuration || 30) * 24 * 60 * 60;
        const speedKmS = distanceKmNum / travelTimeSeconds;
        const speedPercentC = (speedKmS / SPEED_OF_LIGHT) * 100;
        const actualPayload = mission.resourcesGained || mission.expectedPayload || 0;
        const payloadPercent = mission.expectedPayload ? (actualPayload / mission.expectedPayload) * 100 : 100;
        
        detailsHtml = `
          <div class="detail-section">
            <div><span class="detail-label">Captain:</span> <span class="detail-value highlight">${mission.captainName || 'Unknown'}</span></div>
            <div><span class="detail-label">Return Distance:</span> <span class="detail-value">${mission.distanceAU?.toFixed(3) || 'N/A'} AU (${distanceKmNum.toLocaleString(undefined, {maximumFractionDigits: 0})} km)</span></div>
            <div><span class="detail-label">Vehicle Speed:</span> <span class="detail-value">${speedKmS.toFixed(1)} km/s (${speedPercentC.toFixed(4)}% c)</span></div>
            <div><span class="detail-label">Travel Time:</span> <span class="detail-value">${mission.returnDuration || 30} days</span></div>
          </div>
          <div class="detail-section">
            <div><span class="detail-label">Payload Type:</span> <span class="detail-value highlight">${mission.resourceType}</span></div>
            <div><span class="detail-label">Payload Weight:</span> <span class="detail-value">${actualPayload.toFixed(1)} tons (${(actualPayload * 1000).toLocaleString()} kg)</span></div>
            <div><span class="detail-label">% of Target:</span> <span class="detail-value ${payloadPercent >= 100 ? 'profit' : ''}">${payloadPercent.toFixed(1)}%</span></div>
          </div>
        `;
        break;
      }
      
      case MissionPhase.DELIVERING_PAYLOAD: {
        // Show payload info, delivery destination
        const actualPayload = mission.resourcesGained || mission.expectedPayload || 0;
        const payloadPercent = mission.expectedPayload ? (actualPayload / mission.expectedPayload) * 100 : 100;
        const destination = mission.deliveryDestination || DeliveryDestination.LEO;
        
        detailsHtml = `
          <div class="detail-section">
            <div><span class="detail-label">Payload Type:</span> <span class="detail-value highlight">${mission.resourceType}</span></div>
            <div><span class="detail-label">Payload Weight:</span> <span class="detail-value">${actualPayload.toFixed(1)} tons (${(actualPayload * 1000).toLocaleString()} kg)</span></div>
            <div><span class="detail-label">% of Target:</span> <span class="detail-value ${payloadPercent >= 100 ? 'profit' : ''}">${payloadPercent.toFixed(1)}%</span></div>
          </div>
          <div class="detail-section">
            <div><span class="detail-label">Delivery To:</span> <span class="detail-value highlight">${DESTINATION_DISPLAY_NAMES[destination]}</span></div>
            <div><span class="detail-label">Status:</span> <span class="detail-value profit">PAYLOAD TRANSFER IN PROGRESS</span></div>
          </div>
        `;
        break;
      }
      
      case MissionPhase.MISSION_SUCCESS: {
        // Full breakdown: payload, contract value, deviations, costs, profit
        // NOTE: Must match onMissionComplete calculation for consistency
        const actualPayload = mission.resourcesGained ?? mission.expectedPayload ?? 0;
        const payloadPercent = mission.expectedPayload ? (actualPayload / mission.expectedPayload) * 100 : 100;
        
        // Estimate revenue for uncollected success cards; collected missions use stored values.
        const efficiency = this.getMissionCrewEfficiency(mission);
        const resourcePrice = getSpotPrice(this.gameState.data.market, mission.resourceType as ResourceType);
        
        let estimatedRevenue: number;
        let lateModifier = 1.0;
        let daysLate = 0;
        
        if (mission.contractValue) {
          // Contract-based: use crew efficiency modifier (same as onMissionComplete)
          const efficiencyModifier = 0.8 + (efficiency * 0.4); // Range: 0.88 to 1.28
          let baseRevenue = Math.round(mission.contractValue * efficiencyModifier);
          
          // Apply late delivery penalty if applicable
          if (mission.contractDeadline) {
            const missionDuration = this.gameState.data.gameTime - mission.launchTime;
            daysLate = Math.max(0, missionDuration - mission.contractDeadline);
            if (daysLate > 0) {
              const penaltyPercent = Math.min(1.0, daysLate / mission.contractDeadline);
              lateModifier = 1.0 - penaltyPercent;
            }
          }
          
          estimatedRevenue = Math.round(baseRevenue * lateModifier);
        } else if (mission.isContractlessMission && mission.targetDepotId && mission.completionProcessed) {
          estimatedRevenue = mission.revenue ?? 0;
        } else {
          // Market-based
          estimatedRevenue = Math.round(actualPayload * resourcePrice);
        }
        const finalRevenue = mission.revenue ?? estimatedRevenue;
        
        // Calculate profit (costs were deducted at contract signing, revenue added at completion)
        const totalCosts = mission.totalCost || 0;
        const netProfit = finalRevenue - totalCosts;
        
        const destination = mission.deliveryDestination || DeliveryDestination.LEO;
        
        // Show efficiency bonus/penalty
        const efficiencyPercent = mission.contractValue ? ((0.8 + (efficiency * 0.4)) * 100 - 100) : 0;
        const latePenaltyPercent = (1 - lateModifier) * 100;
        
        detailsHtml = `
          <div class="detail-section success-section">
            <div><span class="detail-label">Payload Delivered:</span> <span class="detail-value highlight">${actualPayload.toFixed(1)} tons of ${mission.resourceType}</span></div>
            <div><span class="detail-label">% of Target:</span> <span class="detail-value ${payloadPercent >= 100 ? 'profit' : ''}">${payloadPercent.toFixed(1)}%</span></div>
            <div><span class="detail-label">Delivered To:</span> <span class="detail-value">${DESTINATION_DISPLAY_NAMES[destination]}</span></div>
          </div>
          <div class="detail-section">
            <div><span class="detail-label">${mission.contractValue ? 'Contract Value:' : 'Cash Payout:'}</span> <span class="detail-value">$${(mission.contractValue ?? finalRevenue).toLocaleString()}</span></div>
            ${mission.contractValue ? `<div><span class="detail-label">Crew Efficiency (${mission.crewName}):</span> <span class="detail-value ${efficiencyPercent >= 0 ? 'profit' : 'loss'}">${efficiencyPercent >= 0 ? '+' : ''}${efficiencyPercent.toFixed(1)}%</span></div>` : ''}
            ${daysLate > 0 ? `<div><span class="detail-label">Late Penalty (${daysLate.toFixed(1)} days late):</span> <span class="detail-value loss">-${latePenaltyPercent.toFixed(1)}%</span></div>` : ''}
            <div><span class="detail-label">Final Payout:</span> <span class="detail-value profit">$${finalRevenue.toLocaleString()}</span></div>
            <div><span class="detail-label">Reward Status:</span> <span class="detail-value ${mission.completionProcessed ? 'profit' : 'highlight'}">${mission.completionProcessed ? 'COLLECTED' : 'CLICK TO COLLECT'}</span></div>
          </div>
          <div class="detail-section">
            <div><span class="detail-label">Vehicle Rental (${mission.providerName}):</span> <span class="detail-value">$${(mission.launchCost || 0).toLocaleString()}</span></div>
            <div><span class="detail-label">Crew Rental (${mission.crewName}):</span> <span class="detail-value">$${(mission.crewCost || 0).toLocaleString()}</span></div>
            <div><span class="detail-label">Total Costs:</span> <span class="detail-value">$${totalCosts.toLocaleString()}</span></div>
          </div>
          <div class="detail-section ${netProfit >= 0 ? 'success-section' : 'failure-section'}">
            <div><span class="detail-label">NET PROFIT:</span> <span class="detail-value ${netProfit >= 0 ? 'profit' : 'loss'}">$${netProfit.toLocaleString()}</span></div>
          </div>
        `;
        break;
      }
      
      // Terminal failure states
      case MissionPhase.LAUNCH_ANOMALY:
      case MissionPhase.IN_FLIGHT_ANOMALY:
      case MissionPhase.EXPLOSION_AT_DRILL_SITE: {
        detailsHtml = `
          <div class="detail-section failure-section">
            <div><span class="detail-label">Mission Status:</span> <span class="detail-value loss">MISSION LOST</span></div>
            <div><span class="detail-label">Total Investment Lost:</span> <span class="detail-value loss">$${(mission.totalCost || 0).toLocaleString()}</span></div>
            <div><span class="detail-label">Vehicle:</span> <span class="detail-value">${mission.providerName}</span></div>
            <div><span class="detail-label">Crew:</span> <span class="detail-value">${mission.crewName}</span></div>
          </div>
        `;
        break;
      }
      
      // Pirate encounters
      case MissionPhase.PIRATE_ATTACK_OUTBOUND:
      case MissionPhase.PIRATE_ATTACK_INBOUND: {
        detailsHtml = `
          <div class="detail-section failure-section">
            <div><span class="detail-label">Alert:</span> <span class="detail-value loss">HOSTILE CONTACT DETECTED</span></div>
            <div><span class="detail-label">Captain:</span> <span class="detail-value">${mission.captainName || 'Unknown'}</span></div>
            <div><span class="detail-label">Crew:</span> <span class="detail-value">${mission.crewName}</span></div>
            <div><span class="detail-label">Payload at Risk:</span> <span class="detail-value">${(mission.expectedPayload || 0).toFixed(1)} tons of ${mission.resourceType}</span></div>
          </div>
        `;
        break;
      }
      
      case MissionPhase.PIRATES_DEFEATED: {
        detailsHtml = `
          <div class="detail-section success-section">
            <div><span class="detail-label">Combat Result:</span> <span class="detail-value profit">PIRATES DEFEATED</span></div>
            <div><span class="detail-label">Captain:</span> <span class="detail-value">${mission.captainName || 'Unknown'}</span></div>
            <div><span class="detail-label">Payload Secured:</span> <span class="detail-value">${(mission.expectedPayload || 0).toFixed(1)} tons of ${mission.resourceType}</span></div>
            ${mission.combatResult ? `<div style="margin-top: 8px; font-style: italic; color: var(--text-muted);">${mission.combatResult.narrative}</div>` : ''}
          </div>
        `;
        break;
      }
      
      case MissionPhase.PIRATES_WON:
      case MissionPhase.PAYLOAD_SEIZED: {
        const payloadLost = mission.currentPhase === MissionPhase.PIRATES_WON ? 'ALL' : '80%';
        detailsHtml = `
          <div class="detail-section failure-section">
            <div><span class="detail-label">Combat Result:</span> <span class="detail-value loss">${mission.currentPhase === MissionPhase.PIRATES_WON ? 'TOTAL LOSS' : 'PAYLOAD SEIZED'}</span></div>
            <div><span class="detail-label">Payload Lost:</span> <span class="detail-value loss">${payloadLost}</span></div>
            <div><span class="detail-label">Captain:</span> <span class="detail-value">${mission.captainName || 'Unknown'}</span></div>
            ${mission.combatResult ? `<div style="margin-top: 8px; font-style: italic; color: var(--text-muted);">${mission.combatResult.narrative}</div>` : ''}
          </div>
        `;
        break;
      }
      
      default:
        detailsHtml = '';
    }
    
    details.innerHTML = detailsHtml;

    // Set styling based on terminal state
    content.classList.remove('failure', 'success');
    if (phaseInfo.isTerminal) {
      if (phaseInfo.isSuccess) {
        content.classList.add('success');
      } else {
        content.classList.add('failure');
      }
    }

    // Store mission ID for dismiss handler
    modal.dataset.missionId = missionId;

    // Show modal
    modal.classList.add('visible');
  }

  private closePhaseModal(): void {
    const modal = document.getElementById('phase-modal');
    if (!modal) return;

    const missionId = modal.dataset.missionId;
    modal.classList.remove('visible');

    // If terminal state, remove mission
    if (missionId) {
      const mission = this.gameState.getMission(missionId);
      if (mission) {
        const phaseInfo = PHASE_INFO[mission.currentPhase];
        if (phaseInfo.isTerminal) {
          let shouldRemoveMission = true;
          // Handle completion/failure
          if (phaseInfo.isSuccess) {
            shouldRemoveMission = mission.completionProcessed || this.onMissionComplete(mission);
          } else {
            this.onMissionFailed(mission);
          }
          if (shouldRemoveMission) {
            this.gameState.removeMission(missionId);
            this.updateActiveMissionsList();
          }
        }
      }
    }
  }

  private setupPhaseModal(): void {
    document.getElementById('phase-modal-close')?.addEventListener('click', () => this.closePhaseModal());
    document.getElementById('phase-modal')?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) {
        this.closePhaseModal();
      }
    });
  }

  // === ASSETS SYSTEM ===

  private setupAssets(): void {
    // Open assets modal button
    document.getElementById('open-assets-btn')?.addEventListener('click', () => {
      this.showAssetsModal();
    });

    // Close assets modal
    document.getElementById('assets-modal-close')?.addEventListener('click', () => {
      const modal = document.getElementById('assets-modal');
      if (modal) modal.classList.remove('visible');
    });
    document.getElementById('assets-modal')?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) {
        (e.target as HTMLElement).classList.remove('visible');
      }
    });

    // Subscribe to state changes to update assets panel
    this.gameState.subscribe('assets-panel', () => {
      this.updateAssetsPanel();
    });

    // Initial update
    this.updateAssetsPanel();
  }

  private updateAssetsPanel(): void {
    const level = getCurrentLevel(this.gameState.data.missionsCompleted);
    const lockedDiv = document.getElementById('assets-locked');
    const contentDiv = document.getElementById('assets-content');
    const statusSpan = document.getElementById('assets-status');

    if (level.id >= 2) {
      // Unlocked
      if (lockedDiv) lockedDiv.style.display = 'none';
      if (contentDiv) contentDiv.style.display = 'block';
      if (statusSpan) statusSpan.textContent = '';

      // Update summary
      const summaryDiv = document.getElementById('owned-assets-summary');
      if (summaryDiv) {
        const ownedCount = this.gameState.data.ownedAssets.length;
        const uniqueOwnedCount = new Set(this.gameState.data.ownedAssets).size;
        const totalAssets = ALL_OWNABLE_ASSETS.length;
        summaryDiv.innerHTML = `
          <div class="owned-assets-summary">
            <span class="owned-count">${ownedCount}</span> asset${ownedCount === 1 ? '' : 's'} owned • ${uniqueOwnedCount} / ${totalAssets} types
          </div>
        `;
      }
    } else {
      // Locked
      if (lockedDiv) lockedDiv.style.display = 'block';
      if (contentDiv) contentDiv.style.display = 'none';
      if (statusSpan) statusSpan.textContent = '(Level 2+)';
    }
  }

  private showAssetsModal(): void {
    const modal = document.getElementById('assets-modal');
    const container = document.getElementById('assets-categories');
    const balanceSpan = document.getElementById('assets-modal-balance');

    if (!modal || !container) return;

    // Update balance
    if (balanceSpan) {
      balanceSpan.textContent = formatCurrency(this.gameState.data.balance);
    }

    const level = getCurrentLevel(this.gameState.data.missionsCompleted);
    const categories: AssetCategory[] = ['vehicle', 'equipment', 'crew'];

    container.innerHTML = categories.map(category => {
      const categoryInfo = ASSET_CATEGORY_INFO[category];
      const assets = getAssetsByCategory(category);

      return `
        <div class="asset-category">
          <div class="asset-category-header">
            <span class="asset-category-icon">${categoryInfo.icon}</span>
            <span class="asset-category-name">${categoryInfo.name}</span>
          </div>
          <div class="asset-grid">
            ${assets.map(asset => this.renderAssetCard(asset, level.id)).join('')}
          </div>
        </div>
      `;
    }).join('');

    // Add click handlers for purchase
    container.querySelectorAll('.asset-card.purchasable').forEach(card => {
      card.addEventListener('click', () => {
        const assetId = card.getAttribute('data-asset-id');
        if (assetId) this.purchaseAsset(assetId);
      });
    });

    modal.classList.add('visible');
  }

  private renderAssetCard(asset: OwnableAsset, playerLevel: number): string {
    const ownedCount = this.gameState.getOwnedAssetCount(asset.id);
    const owned = ownedCount > 0;
    const repeatPurchasable = asset.category === 'vehicle' || asset.category === 'crew';
    const canAfford = this.gameState.data.balance >= asset.purchaseCost;
    const levelLocked = asset.unlockLevel > playerLevel;
    const canPurchase = !levelLocked && (!owned || repeatPurchasable);

    let cardClass = 'asset-card';
    if (owned) cardClass += ' owned';
    if (repeatPurchasable) cardClass += ' repeat-purchasable';
    if (canPurchase) cardClass += ' purchasable';
    if (levelLocked) cardClass += ' locked';

    let priceClass = 'asset-price';
    let priceText = '';
    if (levelLocked) {
      priceClass += '';
      priceText = `Level ${asset.unlockLevel}+`;
    } else if (owned && repeatPurchasable) {
      priceClass += canAfford ? ' can-afford' : ' cannot-afford';
      priceText = `Buy another: ${formatCurrency(asset.purchaseCost)}`;
    } else if (owned) {
      priceText = '✓ Purchased';
    } else {
      priceClass += canAfford ? ' can-afford' : ' cannot-afford';
      priceText = formatCurrency(asset.purchaseCost);
    }

    return `
      <div class="${cardClass}" data-asset-id="${asset.id}">
        <div class="asset-name">${asset.name}</div>
        <div class="asset-desc">${asset.description}</div>
        <div class="asset-stats">
          ${owned ? `<span class="asset-owned-count">Owned: ${ownedCount}</span>` : ''}
          <span class="${priceClass}">${priceText}</span>
          <span class="asset-breakeven">${asset.breakEvenMissions} missions to break even</span>
        </div>
      </div>
    `;
  }

  private purchaseAsset(assetId: string): void {
    const asset = getAssetById(assetId);
    if (!asset) return;

    const allowDuplicate = asset.category === 'vehicle' || asset.category === 'crew';
    const result = this.gameState.purchaseAsset(assetId, asset.purchaseCost, allowDuplicate);
    if (result.success) {
      // Track in AssetManager for mission planning
      this.assetManager.purchaseAsset(assetId);
      
      this.addNewsItem(`Purchased ${asset.name} for ${formatCurrency(asset.purchaseCost)}`, 'important');
      // Refresh modal
      this.showAssetsModal();
      this.updateAssetsPanel();
    } else {
      this.addNewsItem(`Cannot purchase: ${result.error}`, 'critical');
    }
  }

  // === STORAGE SYSTEM ===

  private setupStorage(): void {
    // Open storage modal button
    document.getElementById('open-storage-btn')?.addEventListener('click', () => {
      this.showStorageModal();
    });

    // Close storage modal
    document.getElementById('storage-modal-close')?.addEventListener('click', () => {
      const modal = document.getElementById('storage-modal');
      if (modal) modal.classList.remove('visible');
    });
    document.getElementById('storage-modal')?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) {
        (e.target as HTMLElement).classList.remove('visible');
      }
    });

    // Subscribe to state changes to update storage panel
    this.gameState.subscribe('storage-panel', () => {
      this.updateStoragePanel();
    });

    // Initial update
    this.updateStoragePanel();
  }

  private updateStoragePanel(): void {
    const level = getCurrentLevel(this.gameState.data.missionsCompleted);
    const lockedDiv = document.getElementById('storage-locked');
    const contentDiv = document.getElementById('storage-content');
    const statusSpan = document.getElementById('storage-status');

    if (level.id >= 3) {
      // Unlocked
      if (lockedDiv) lockedDiv.style.display = 'none';
      if (contentDiv) contentDiv.style.display = 'block';
      if (statusSpan) statusSpan.textContent = '';

      // Update summary
      const summaryDiv = document.getElementById('storage-summary');
      if (summaryDiv) {
        const storage = this.gameState.data.storage;
        const ownedCount = Object.values(storage.depots).reduce((sum, d) => sum + d.count, 0);
        const totalCapacity = getTotalStorageCapacityNew(storage);
        const usedCapacity = getTotalStoredTonsNew(storage);
        
        if (ownedCount === 0) {
          summaryDiv.innerHTML = `<div class="storage-summary">No depots owned yet</div>`;
        } else {
          summaryDiv.innerHTML = `
            <div class="storage-summary">
              <span class="storage-count">${ownedCount}</span> depot${ownedCount !== 1 ? 's' : ''} • 
              ${formatCapacity(usedCapacity, totalCapacity)}
            </div>
          `;
        }
      }
    } else {
      // Locked
      if (lockedDiv) lockedDiv.style.display = 'block';
      if (contentDiv) contentDiv.style.display = 'none';
      if (statusSpan) statusSpan.textContent = '(Level 3+)';
    }
  }

  private showStorageModal(): void {
    const modal = document.getElementById('storage-modal');
    const container = document.getElementById('storage-depots-list');
    const balanceSpan = document.getElementById('storage-modal-balance');

    if (!modal || !container) return;

    // Update balance
    if (balanceSpan) {
      balanceSpan.textContent = formatCurrency(this.gameState.data.balance);
    }

    const level = getCurrentLevel(this.gameState.data.missionsCompleted);
    const storage = this.gameState.data.storage;

    container.innerHTML = STORAGE_DEPOTS.map(depot => {
      return this.renderDepotCard(depot, level.id, storage);
    }).join('');

    // Add click handlers for purchase
    container.querySelectorAll('.depot-buy-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const depotId = btn.getAttribute('data-depot-id');
        if (depotId) this.purchaseDepot(depotId);
      });
    });

    // Add click handlers for sell buttons
    container.querySelectorAll('.depot-sell-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const depotId = btn.getAttribute('data-depot-id');
        const resource = btn.getAttribute('data-resource');
        if (depotId && resource) this.showSellModal(depotId, resource);
      });
    });

    modal.classList.add('visible');
  }

  private renderDepotCard(depot: StorageDepot, playerLevel: number, storage: any): string {
    const ownedCount = this.gameState.getDepotCount(depot.id);
    const canAfford = this.gameState.data.balance >= depot.purchaseCost;
    const levelLocked = depot.unlockLevel > playerLevel;

    let cardClass = 'depot-card';
    if (ownedCount > 0) cardClass += ' owned';
    else if (levelLocked) cardClass += ' locked';

    // Get instance info if owned
    const instance = storage.depots[depot.id];
    const totalCapacity = instance ? getInstanceTotalCapacity(depot.id, instance.count) : 0;
    const usedCapacity = instance ? getDepotUsedCapacity(instance.contents) : 0;
    const percent = totalCapacity > 0 ? Math.round((usedCapacity / totalCapacity) * 100) : 0;

    // Build status/buy section
    let statusSection = '';
    if (ownedCount > 0) {
      statusSection = `
        <div class="depot-owned-status">
          <span class="depot-owned-count">✓ ${ownedCount} owned</span>
          ${!levelLocked && canAfford ? `
            <button class="depot-buy-btn" data-depot-id="${depot.id}">
              Buy Another (${formatCurrency(depot.purchaseCost)})
            </button>
          ` : ''}
        </div>
      `;
    } else if (levelLocked) {
      statusSection = `<div class="depot-locked-status">🔒 Level ${depot.unlockLevel}+</div>`;
    } else {
      statusSection = `
        <button class="depot-buy-btn ${canAfford ? 'can-afford' : 'cannot-afford'}" data-depot-id="${depot.id}">
          ${canAfford ? 'Buy' : 'Need'} ${formatCurrency(depot.purchaseCost)}
        </button>
      `;
    }

    // Build capacity bar and contents if owned
    let capacitySection = '';
    if (ownedCount > 0 && instance) {
      const fillClass = percent >= 90 ? 'full' : percent >= 70 ? 'warning' : '';
      
      // Build stored resources list
      const contents = instance.contents;
      const storedItems: string[] = [];
      for (const [resource, amount] of Object.entries(contents)) {
        if ((amount as number) > 0) {
          const displayName = MARKET_RESOURCE_INFO[resource as keyof typeof MARKET_RESOURCE_INFO]?.name || resource;
          const spotPrice = getSpotPrice(this.gameState.data.market, resource as ResourceType);
          storedItems.push(`
            <div class="depot-stored-item">
              <span class="stored-resource">${displayName}</span>
              <span class="stored-amount">${(amount as number).toLocaleString()} tons</span>
              <span class="stored-value">(~${formatCurrency((amount as number) * spotPrice)})</span>
              <button class="depot-sell-btn" data-depot-id="${depot.id}" data-resource="${resource}">Sell</button>
            </div>
          `);
        }
      }

      capacitySection = `
        <div class="depot-capacity-section">
          <div class="depot-capacity-header">
            ${formatCapacity(usedCapacity, totalCapacity)} (${ownedCount} × ${depot.capacity.toLocaleString()} tons)
          </div>
          <div class="depot-capacity-bar">
            <div class="depot-capacity-fill ${fillClass}" style="width: ${percent}%"></div>
          </div>
          ${storedItems.length > 0 ? `
            <div class="depot-stored-list">${storedItems.join('')}</div>
          ` : `
            <div class="depot-empty">Empty - use spec-free mining to fill</div>
          `}
        </div>
      `;
    }

    return `
      <div class="${cardClass}" data-depot-id="${depot.id}">
        <div class="depot-header">
          <div>
            <div class="depot-name">${depot.name}</div>
            <div class="depot-location">📍 ${depot.location}</div>
          </div>
          ${statusSection}
        </div>
        <div class="depot-desc">${depot.description}</div>
        <div class="depot-stats">
          <div class="depot-stat">
            <div class="depot-stat-label">Capacity Each</div>
            <div class="depot-stat-value">${depot.capacity.toLocaleString()} tons</div>
          </div>
          <div class="depot-stat">
            <div class="depot-stat-label">Travel Time</div>
            <div class="depot-stat-value">${depot.travelTimeFromEarth} days</div>
          </div>
          <div class="depot-stat">
            <div class="depot-stat-label">Security Risk</div>
            <div class="depot-stat-value">${depot.securityRisk}</div>
          </div>
        </div>
        ${capacitySection}
      </div>
    `;
  }

  private showSellModal(depotId: string, resource: string): void {
    const depot = getDepotById(depotId);
    const instance = this.gameState.data.storage.depots[depotId];
    if (!depot || !instance) return;

    const available = instance.contents[resource as keyof typeof instance.contents] || 0;
    if (available <= 0) return;

    const spotPrice = getSpotPrice(this.gameState.data.market, resource as ResourceType);
    const resourceInfo = MARKET_RESOURCE_INFO[resource as keyof typeof MARKET_RESOURCE_INFO];
    const displayName = resourceInfo?.name || resource;

    // Create or update sell modal
    let modal = document.getElementById('sell-resource-modal');
    if (!modal) {
      modal = document.createElement('div');
      modal.id = 'sell-resource-modal';
      modal.className = 'modal-overlay';
      document.body.appendChild(modal);
    }

    modal.innerHTML = `
      <div class="modal-content sell-modal">
        <div class="modal-header">
          <h2>Sell ${displayName}</h2>
          <button class="modal-close" id="sell-modal-close">✕</button>
        </div>
        <div class="sell-modal-body">
          <div class="sell-info">
            <div class="sell-depot">From: ${depot.name}</div>
            <div class="sell-available">Available: ${available.toLocaleString()} tons</div>
            <div class="sell-price">Market Price: ${formatCurrency(spotPrice)}/ton</div>
          </div>
          <div class="sell-amount-section">
            <label>Amount to sell:</label>
            <input type="number" id="sell-amount" min="1" max="${available}" value="${available}" />
            <div class="sell-quick-buttons">
              <button class="sell-quick" data-pct="25">25%</button>
              <button class="sell-quick" data-pct="50">50%</button>
              <button class="sell-quick" data-pct="100">All</button>
            </div>
          </div>
          <div class="sell-total">
            Total: <span id="sell-total-value">${formatCurrency(available * spotPrice)}</span>
          </div>
        </div>
        <div class="modal-actions">
          <button class="modal-btn secondary" id="sell-cancel">Cancel</button>
          <button class="modal-btn primary" id="sell-confirm">Confirm Sale</button>
        </div>
      </div>
    `;

    // Add event handlers
    const amountInput = modal.querySelector('#sell-amount') as HTMLInputElement;
    const totalSpan = modal.querySelector('#sell-total-value');
    
    const updateTotal = () => {
      const amount = Math.min(available, Math.max(0, parseInt(amountInput.value) || 0));
      if (totalSpan) totalSpan.textContent = formatCurrency(amount * spotPrice);
    };

    amountInput?.addEventListener('input', updateTotal);

    modal.querySelectorAll('.sell-quick').forEach(btn => {
      btn.addEventListener('click', () => {
        const pct = parseInt(btn.getAttribute('data-pct') || '100');
        amountInput.value = String(Math.floor(available * pct / 100));
        updateTotal();
      });
    });

    modal.querySelector('#sell-modal-close')?.addEventListener('click', () => {
      modal?.classList.remove('visible');
    });
    modal.querySelector('#sell-cancel')?.addEventListener('click', () => {
      modal?.classList.remove('visible');
    });

    modal.querySelector('#sell-confirm')?.addEventListener('click', () => {
      const amount = Math.min(available, Math.max(0, parseInt(amountInput.value) || 0));
      if (amount > 0) {
        const result = this.gameState.sellFromDepot(depotId, resource, amount, spotPrice);
        if (result.success) {
          this.addNewsItem(`Sold ${amount.toLocaleString()} tons of ${displayName} for ${formatCurrency(result.revenue)}`, 'important');
          modal?.classList.remove('visible');
          this.showStorageModal(); // Refresh storage modal
        }
      }
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.classList.remove('visible');
    });

    modal.classList.add('visible');
  }

  private purchaseDepot(depotId: string): void {
    const depot = getDepotById(depotId);
    if (!depot) return;

    const result = this.gameState.purchaseDepot(depotId, depot.purchaseCost);
    if (result.success) {
      this.addNewsItem(`Purchased ${depot.name} for ${formatCurrency(depot.purchaseCost)}`, 'important');
      // Refresh modal
      this.showStorageModal();
      this.updateStoragePanel();
    } else {
      this.addNewsItem(`Cannot purchase: ${result.error}`, 'critical');
    }
  }

  // === LEVEL TRANSITIONS ===

  private setupLevelTransitions(): void {
    // Subscribe to state changes to detect level ups
    this.gameState.subscribe('level-transitions', (data) => {
      const newLevel = getCurrentLevel(data.missionsCompleted).id;
      if (newLevel > this.lastKnownLevel) {
        this.showLevelUpCeremony(newLevel);
        this.lastKnownLevel = newLevel;
      }
    });

    // Close level-up modal button
    document.getElementById('level-up-continue')?.addEventListener('click', () => {
      this.closeLevelUpModal();
    });
  }

  private showLevelUpCeremony(levelId: number): void {
    const level = PROGRESSION_LEVELS.find(l => l.id === levelId);
    if (!level) return;

    const modal = document.getElementById('level-up-modal');
    const badge = document.getElementById('level-up-badge');
    const title = document.getElementById('level-up-title');
    const description = document.getElementById('level-up-description');
    const unlocks = document.getElementById('level-up-unlocks');

    if (!modal) return;

    // Set level-specific background image
    const levelImages: Record<number, string> = {
      2: '/images/mining_jupiter_moon.png',
      3: '/images/mining_pirate_approach.png',
      4: '/images/mining_fleet_commander.png',
      5: '/images/mining_milky_way.png'
    };
    const bgImage = levelImages[levelId] || '/images/mining_red_asteroid.png';
    modal.style.backgroundImage = `url('${bgImage}')`;
    modal.style.backgroundSize = 'cover';
    modal.style.backgroundPosition = 'center';
    modal.style.backgroundRepeat = 'no-repeat';

    // Populate content
    if (badge) badge.textContent = `LEVEL ${level.id}`;
    if (title) title.textContent = level.displayName;
    if (description) {
      // Keep HTML links in description
      description.innerHTML = level.description;
    }

    // Populate unlocks
    if (unlocks && level.unlocks.length > 0) {
      unlocks.innerHTML = `
        <h3>New Features Unlocked:</h3>
        <ul>
          ${level.unlocks.map(u => `<li>${u}</li>`).join('')}
        </ul>
      `;
      unlocks.style.display = 'block';
    } else if (unlocks) {
      unlocks.style.display = 'none';
    }

    // Show modal
    modal.classList.add('visible');

    // Add news announcement
    this.addNewsItem(`🎉 LEVEL UP! Welcome to ${level.displayName}!`, 'important');

    // Play audio fanfare (placeholder - audio file TBD)
    this.playLevelUpSound(levelId);
  }

  private closeLevelUpModal(): void {
    const modal = document.getElementById('level-up-modal');
    if (modal) {
      modal.classList.remove('visible');
    }
    
    // Trigger intro zoom effect for the new level
    if (this.solarSystem) {
      this.solarSystem.startIntroZoom();
    }
  }

  private playLevelUpSound(_levelId: number): void {
    // Placeholder for audio playback
    // TODO: Add audio files and implement playback
    // const audio = new Audio(`/assets/audio/level_up_${levelId}.mp3`);
    // audio.play().catch(() => {}); // Ignore autoplay restrictions
    console.log('Level up sound would play here');
  }

  // ==================== MARKET SYSTEM ====================

  private lastMarketUpdate: number = 0;

  private setupMarket(): void {
    // Initial render of market prices
    this.renderMarketPrices();

    // Initial render of flight log
    this.renderFlightLog();

    // Setup market detail modal close button
    const closeBtn = document.getElementById('market-detail-close');
    const modal = document.getElementById('market-detail-modal');
    if (closeBtn && modal) {
      closeBtn.addEventListener('click', () => {
        modal.classList.remove('visible');
      });
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          modal.classList.remove('visible');
        }
      });
    }

    // Subscribe to game state changes to update market
    this.gameState.subscribe('market-updates', () => {
      const currentTime = this.gameState.data.gameTime;
      // Update market prices every game week (but check every ~minute real time to avoid spam)
      if (currentTime - this.lastMarketUpdate >= 7) {
        // Update lastMarketUpdate BEFORE calling updateMarket to prevent recursion
        this.lastMarketUpdate = currentTime;
        this.updateMarket();
      }
    });
  }

  private updateMarket(): void {
    const currentTime = this.gameState.data.gameTime;
    const { market, newsEvents } = updateMarketPrices(
      this.gameState.data.market,
      currentTime
    );

    // Update game state with new market
    this.gameState.update({ market });

    // Announce significant price changes
    newsEvents.forEach(news => {
      this.addNewsItem(news, 'market');
    });

    // Re-render market display
    this.renderMarketPrices();
  }

  private renderMarketPrices(): void {
    const container = document.getElementById('market-prices');
    if (!container) return;

    const market = this.gameState.data.market;
    const sortedResources = getResourcesByPrice(market);

    container.innerHTML = sortedResources.map(resource => {
      const info = RESOURCE_INFO[resource];
      const price = getSpotPrice(market, resource);
      const trend = getPriceTrend(market, resource);
      const trendIcon = trend === 'up' ? '▲' : trend === 'down' ? '▼' : '•';
      const history = market.resources[resource].priceHistory;
      
      // Generate mini sparkline SVG
      const sparkline = this.generateSparkline(history);

      return `
        <div class="market-row" data-resource="${resource}">
          <div class="market-resource">
            <span class="market-icon">${info.icon}</span>
            <span class="market-name">${info.name}</span>
          </div>
          <div style="display: flex; align-items: center;">
            ${sparkline}
            <span class="market-price ${trend}">
              ${formatPrice(price)}
              <span class="market-trend">${trendIcon}</span>
            </span>
          </div>
        </div>
      `;
    }).join('');

    // Add click handlers
    container.querySelectorAll('.market-row').forEach(row => {
      row.addEventListener('click', () => {
        const resource = row.getAttribute('data-resource') as ResourceType;
        this.showMarketDetail(resource);
      });
    });
  }

  private generateSparkline(history: { price: number }[]): string {
    if (history.length < 2) return '';

    const width = 40;
    const height = 16;
    const prices = history.map(h => h.price);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const range = max - min || 1;

    const points = prices.map((price, i) => {
      const x = (i / (prices.length - 1)) * width;
      const y = height - ((price - min) / range) * height;
      return `${x},${y}`;
    }).join(' ');

    const lastPrice = prices[prices.length - 1];
    const firstPrice = prices[0];
    const color = lastPrice >= firstPrice ? '#4ade80' : '#f87171';

    return `
      <svg class="market-sparkline" viewBox="0 0 ${width} ${height}">
        <polyline 
          fill="none" 
          stroke="${color}" 
          stroke-width="1.5"
          points="${points}"
        />
      </svg>
    `;
  }

  private showMarketDetail(resource: ResourceType): void {
    const modal = document.getElementById('market-detail-modal');
    const info = RESOURCE_INFO[resource];
    const market = this.gameState.data.market;
    const resourceMarket = market.resources[resource];
    const basePrice = RESOURCE_BASE_PRICES[resource];

    if (!modal) return;

    // Update modal content
    const iconEl = document.getElementById('market-detail-icon');
    const nameEl = document.getElementById('market-detail-name');
    const currentEl = document.getElementById('market-current-price');
    const baseEl = document.getElementById('market-base-price');
    const highEl = document.getElementById('market-high');
    const lowEl = document.getElementById('market-low');

    if (iconEl) iconEl.textContent = info.icon;
    if (nameEl) nameEl.textContent = info.name;
    if (currentEl) currentEl.textContent = formatPrice(resourceMarket.currentPrice);
    if (baseEl) baseEl.textContent = formatPrice(basePrice);

    const prices = resourceMarket.priceHistory.map(h => h.price);
    if (highEl) highEl.textContent = formatPrice(Math.max(...prices));
    if (lowEl) lowEl.textContent = formatPrice(Math.min(...prices));

    // Draw chart
    this.drawMarketChart(resource);

    modal.classList.add('visible');
  }

  private drawMarketChart(resource: ResourceType): void {
    const canvas = document.getElementById('market-chart-canvas') as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const market = this.gameState.data.market;
    const history = market.resources[resource].priceHistory;
    const basePrice = RESOURCE_BASE_PRICES[resource];

    // Set canvas size
    const container = canvas.parentElement;
    if (container) {
      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;
    }

    const width = canvas.width;
    const height = canvas.height;
    const padding = 10;

    // Clear
    ctx.clearRect(0, 0, width, height);

    if (history.length < 2) {
      ctx.fillStyle = UI_COLORS.MUTED;
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Not enough data', width / 2, height / 2);
      return;
    }

    const prices = history.map(h => h.price);
    const min = Math.min(...prices, basePrice * 0.5);
    const max = Math.max(...prices, basePrice * 1.5);
    const range = max - min || 1;

    // Draw base price line
    const baseY = height - padding - ((basePrice - min) / range) * (height - padding * 2);
    ctx.strokeStyle = 'rgba(84, 230, 255, 0.3)';
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(padding, baseY);
    ctx.lineTo(width - padding, baseY);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw price line
    const lastPrice = prices[prices.length - 1];
    const firstPrice = prices[0];
    ctx.strokeStyle = lastPrice >= firstPrice ? '#4ade80' : '#f87171';
    ctx.lineWidth = 2;
    ctx.beginPath();

    prices.forEach((price, i) => {
      const x = padding + (i / (prices.length - 1)) * (width - padding * 2);
      const y = height - padding - ((price - min) / range) * (height - padding * 2);
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.stroke();

    // Draw current price dot
    const lastX = width - padding;
    const lastY = height - padding - ((lastPrice - min) / range) * (height - padding * 2);
    ctx.fillStyle = lastPrice >= firstPrice ? '#4ade80' : '#f87171';
    ctx.beginPath();
    ctx.arc(lastX, lastY, 4, 0, Math.PI * 2);
    ctx.fill();
  }

  private renderFlightLog(): void {
    const container = document.getElementById('flight-log');
    if (!container) return;

    const log = this.gameState.data.flightLog;
    
    if (log.length === 0) {
      container.innerHTML = '<p style="color: #888; font-size: 11px;">No completed missions yet</p>';
      return;
    }

    // Show most recent first
    container.innerHTML = [...log].reverse().map(entry => {
      const profitClass = entry.profit >= 0 ? '' : 'loss';
      const profitPrefix = entry.profit >= 0 ? '+' : '';
      return `
        <div class="flight-log-entry">
          <div class="flight-log-asteroid">${entry.asteroidName}</div>
          <div class="flight-log-details">
            <span>${entry.tons.toLocaleString()} tons ${entry.resourceType}</span>
            <span class="flight-log-profit ${profitClass}">${profitPrefix}${formatCurrency(entry.profit)}</span>
          </div>
        </div>
      `;
    }).join('');
  }

  // ==================== END MARKET SYSTEM ====================

  private updateMissions(): void {
    const currentTime = this.gameState.data.gameTime;
    const missions = this.gameState.data.activeMissions;
    let needsUpdate = false;

    missions.forEach(mission => {
      const phaseInfo = PHASE_INFO[mission.currentPhase];
      
      // Skip terminal states
      if (phaseInfo.isTerminal) return;

      // Calculate overall progress
      const elapsed = currentTime - mission.launchTime;
      const progress = Math.min(1, elapsed / mission.totalDuration);

      // Check if current phase is complete
      const phaseElapsed = currentTime - mission.phaseStartTime;
      
      if (phaseElapsed >= mission.phaseDuration) {
        const provider = LAUNCH_PROVIDERS.find(p => p.id === mission.providerId);
        const playerLevel = getCurrentLevel(this.gameState.data.missionsCompleted).id;
        
        let nextPhase: MissionPhase;
        let combatResult: CombatResult | undefined;
        
        // Handle pirate attack phases - resolve combat
        if (mission.currentPhase === MissionPhase.PIRATE_ATTACK_OUTBOUND || 
            mission.currentPhase === MissionPhase.PIRATE_ATTACK_INBOUND) {
          // Resolve combat
          combatResult = resolveCombat(
            playerLevel,
            mission.securityId || null,
            this.gameState.data.securityRelationships?.[mission.securityId || ''] || 0
          );
          
          // News about combat
          this.addNewsItem(formatCombatNews(combatResult, mission.asteroidName), 
            combatResult.outcome === 'pirates_defeated' ? 'important' : 'critical');
          
          // Determine next phase based on combat outcome
          if (combatResult.outcome === 'pirates_defeated') {
            // Continue mission - go to next normal phase
            if (mission.currentPhase === MissionPhase.PIRATE_ATTACK_OUTBOUND) {
              nextPhase = MissionPhase.DRILLING;
            } else {
              nextPhase = MissionPhase.DELIVERING_PAYLOAD;
            }
          } else if (combatResult.outcome === 'pirates_won') {
            nextPhase = MissionPhase.PIRATES_WON;
          } else {
            nextPhase = MissionPhase.PAYLOAD_SEIZED;
          }
        } else {
          // Normal phase transition - roll for next phase
          nextPhase = rollNextPhase(
            mission.currentPhase,
            mission.providerReliability,
            mission.crewReliability
          );
          
          // Easter egg: Sun missions always fail during outbound
          if (mission.asteroidId === 'sun' && mission.currentPhase === MissionPhase.OUTBOUND) {
            nextPhase = MissionPhase.IN_FLIGHT_ANOMALY;
            this.addNewsItem(`☀️ Mission to the Sun: "It's getting really hot in here..." - Last transmission from Captain ${mission.captainName}`, 'critical');
          }
          
          // Check for pirate attacks at Level 3+ when entering certain phases
          if (playerLevel >= 3) {
            // Check when completing OUTBOUND (before DRILLING)
            if (mission.currentPhase === MissionPhase.OUTBOUND && nextPhase === MissionPhase.DRILLING) {
              if (checkPirateAttack('outbound', playerLevel)) {
                nextPhase = MissionPhase.PIRATE_ATTACK_OUTBOUND;
                this.addNewsItem(`⚠️ Pirates detected near ${mission.asteroidName}!`, 'critical');
              }
            }
            // Check when completing DRILLING (before INBOUND) - actually check at end of inbound
            // Check when completing INBOUND (before DELIVERING)
            if (mission.currentPhase === MissionPhase.INBOUND && nextPhase === MissionPhase.DELIVERING_PAYLOAD) {
              if (checkPirateAttack('inbound', playerLevel)) {
                nextPhase = MissionPhase.PIRATE_ATTACK_INBOUND;
                this.addNewsItem(`⚠️ Pirates intercepting ${mission.asteroidName} cargo ship!`, 'critical');
              }
            }
          }
        }
        
        const nextPhaseInfo = PHASE_INFO[nextPhase];
        
        // Calculate next phase duration
        const nextPhaseDuration = nextPhaseInfo.isTerminal ? 0 : calculatePhaseDuration(
          nextPhase,
          mission.outboundDuration,
          mission.miningDuration,
          mission.returnDuration,
          provider?.launchFrequency || 'Weekly'
        );

        // Build update object
        const missionUpdate: Partial<Mission> = {
          currentPhase: nextPhase,
          phaseStartTime: currentTime,
          phaseDuration: nextPhaseDuration,
          phaseJustChanged: true,
          progress,
        };
        
        // Add combat result if applicable
        if (combatResult) {
          missionUpdate.combatResult = {
            outcome: combatResult.outcome,
            narrative: combatResult.narrative,
          };
        }

        // Update mission
        this.gameState.updateMission(mission.id, missionUpdate);

        needsUpdate = true;

        // Handle vehicle loss on anomaly phases
        const lostOwnedVehicle = this.assetManager.isVehicleLossPhase(nextPhase) && !!mission.ownedVehicleId;
        if (lostOwnedVehicle) {
          const lostVehicle = this.assetManager.handleMissionAnomaly(mission);
          if (lostVehicle) {
            this.addNewsItem(`💥 Your ${lostVehicle.name} was destroyed in the ${nextPhaseInfo.name}!`, 'critical');
          }
        }

        // News item for phase change
        if (nextPhaseInfo.isTerminal) {
          if (nextPhaseInfo.isSuccess) {
            this.addNewsItem(`Mission to ${mission.asteroidName}: SUCCESS!`, 'important');
            // Release owned assets back to pool on success
            if (mission.ownedVehicleId || mission.ownedCrewId) {
              this.assetManager.handleMissionComplete(mission);
            }
          } else if (nextPhase !== MissionPhase.PIRATES_WON && nextPhase !== MissionPhase.PAYLOAD_SEIZED) {
            if (!lostOwnedVehicle && (mission.ownedVehicleId || mission.ownedCrewId)) {
              this.assetManager.handleMissionComplete(mission);
            }
            // Don't duplicate news for pirate outcomes (already shown above)
            this.addNewsItem(`Mission to ${mission.asteroidName}: ${nextPhaseInfo.name}`, 'critical');
          } else if (!lostOwnedVehicle && (mission.ownedVehicleId || mission.ownedCrewId)) {
            this.assetManager.handleMissionComplete(mission);
          }
        } else if (nextPhase === MissionPhase.DRILLING) {
          // Mark asteroid as mined when drilling begins
          if (!this.gameState.data.minedAsteroids.includes(mission.asteroidId)) {
            this.gameState.update({
              minedAsteroids: [...this.gameState.data.minedAsteroids, mission.asteroidId]
            });
            // Hide label for this asteroid
            if (this.solarSystem) {
              this.solarSystem.setAsteroidMined(mission.asteroidId, true);
            }
          }
        } else if (nextPhase === MissionPhase.LAUNCH) {
          this.addNewsItem(`Mission to ${mission.asteroidName} launching!`, 'market');
        }
      } else {
        // Just update progress internally (don't trigger list rebuild)
        const roundedProgress = Math.round(progress * 100) / 100;
        if (roundedProgress !== Math.round(mission.progress * 100) / 100) {
          this.gameState.updateMission(mission.id, { progress, phaseJustChanged: false });
          // Only rebuild list if progress changed by 1% or more (visual update)
          needsUpdate = true;
        }
      }
    });

    if (needsUpdate) {
      this.updateActiveMissionsList();
    }
  }

  private onMissionFailed(mission: Mission): void {
    // Handle PAYLOAD_SEIZED - partial failure, crew survives but cargo lost
    if (mission.currentPhase === MissionPhase.PAYLOAD_SEIZED) {
      // No revenue, but increment mission count (experience gained)
      this.gameState.update({
        missionsCompleted: this.gameState.data.missionsCompleted + 1,
      });
      
      // Add to flight log with zero profit
      const logEntry: FlightLogEntry = {
        asteroidName: mission.asteroidName,
        resourceType: mission.resourceType || 'Unknown',
        tons: 0,
        profit: -mission.totalCost,
        completedTime: this.gameState.data.gameTime,
      };
      const newLog = [...this.gameState.data.flightLog, logEntry].slice(-3);
      this.gameState.update({ flightLog: newLog });
      this.renderFlightLog();
    }
    
    // Increment security relationship if security was hired (regardless of outcome)
    if (mission.securityId) {
      const currentRel = this.gameState.data.securityRelationships[mission.securityId] || 0;
      if (currentRel < 10) {
        this.gameState.update({
          securityRelationships: {
            ...this.gameState.data.securityRelationships,
            [mission.securityId]: Math.min(10, currentRel + 1),
          },
        });
      }
    }
  }

  private onMissionComplete(mission: Mission): boolean {
    const currentMission = this.gameState.getMission(mission.id) || mission;
    if (currentMission.completionProcessed) {
      return true;
    }
    mission = currentMission;

    const asteroid = this.getMissionAsteroid(mission);
    const efficiency = this.getMissionCrewEfficiency(mission);
    const resourcesGained = mission.expectedPayload
      ?? (asteroid ? estimateResourceYield(asteroid.diameter, asteroid.taxonomicClass, efficiency, asteroid.mass) : 0);
    
    if (resourcesGained <= 0) {
      console.error(`[Mission] Cannot complete ${mission.id}: no payload data available`);
      this.addNewsItem(`Mission to ${mission.asteroidName} could not be completed: payload data unavailable.`, 'critical');
      return false;
    }
    
    let revenue: number;
    let profit: number;
    let newsMessage: string;
    let resourceTypeForLog = mission.resourceType || 'Mixed';
    let updatedStorage: GameStateData['storage'] | undefined;

    if (mission.isContractlessMission && mission.targetDepotId) {
      if (!asteroid && !this.isResourceType(mission.resourceType)) {
        console.error(`[Mission] Cannot complete ${mission.id}: asteroid composition unavailable`);
        this.addNewsItem(`Mission to ${mission.asteroidName} could not be completed: asteroid composition unavailable.`, 'critical');
        return false;
      }

      // Spec-free mining: fill all owned depots, overflow sold at market
      const storage = this.gameState.data.storage;
      
      // Pick random resource from asteroid's composition
      const availableResources = asteroid ? getPrimaryResourcesForType(asteroid.taxonomicClass) : [];
      const resourceType = availableResources.length > 0
        ? availableResources[Math.floor(Math.random() * availableResources.length)]
        : mission.resourceType as ResourceType;
      resourceTypeForLog = resourceType;
      
      // Fill ALL owned depots in order until full, then sell overflow
      let remainingToStore = resourcesGained;
      let totalStored = 0;
      const newDepots = { ...storage.depots };
      
      // Get all owned depot IDs sorted by capacity (fill smaller depots first)
      const ownedDepotIds = Object.keys(storage.depots)
        .filter(id => storage.depots[id].count > 0)
        .sort((a, b) => {
          const depotA = getDepotById(a);
          const depotB = getDepotById(b);
          return (depotA?.capacity || 0) - (depotB?.capacity || 0);
        });
      
      for (const depotId of ownedDepotIds) {
        if (remainingToStore <= 0) break;
        
        const depot = getDepotById(depotId);
        const instance = storage.depots[depotId];
        if (!depot || !instance) continue;
        
        const depotContents = instance.contents || createEmptyStorage();
        const totalCapacity = depot.capacity * instance.count;
        const usedCapacity = Object.values(depotContents).reduce((s, v) => s + v, 0);
        const remainingCapacity = totalCapacity - usedCapacity;
        
        if (remainingCapacity > 0) {
          const storeInThisDepot = Math.min(remainingToStore, remainingCapacity);
          const currentAmount = depotContents[resourceType] || 0;
          
          newDepots[depotId] = {
            ...instance,
            contents: {
              ...depotContents,
              [resourceType]: currentAmount + storeInThisDepot,
            },
          };
          
          totalStored += storeInThisDepot;
          remainingToStore -= storeInThisDepot;
        }
      }
      
      const overflow = remainingToStore;
      updatedStorage = { depots: newDepots };
      
      // Sell overflow at market price
      const market = this.gameState.data.market;
      const spotPrice = getSpotPrice(market, resourceType);
      revenue = overflow > 0 ? Math.round(overflow * spotPrice) : 0;
      profit = revenue - mission.totalCost;
      
      if (overflow > 0) {
        newsMessage = `Mission to ${mission.asteroidName} complete! Stored ${totalStored.toLocaleString()} tons, sold ${overflow.toLocaleString()} tons overflow for ${formatCurrency(revenue)}`;
      } else {
        newsMessage = `Mission to ${mission.asteroidName} complete! Stored ${totalStored.toLocaleString()} tons of ${resourceType}`;
        // No revenue for pure storage missions
        profit = -mission.totalCost;
      }
    } else if (mission.contractValue) {
      // Contract-based payout with crew efficiency modifier
      const efficiencyModifier = 0.8 + (efficiency * 0.4); // Range: 0.88 to 1.28
      const baseRevenue = Math.round(mission.contractValue * efficiencyModifier);
      
      // Apply late delivery penalty if applicable
      let lateModifier = 1.0;
      if (mission.contractDeadline) {
        const missionDuration = this.gameState.data.gameTime - mission.launchTime;
        const daysLate = Math.max(0, missionDuration - mission.contractDeadline);
        if (daysLate > 0) {
          // Penalty = daysLate / deadline, capped at 100%
          const penaltyPercent = Math.min(1.0, daysLate / mission.contractDeadline);
          lateModifier = 1.0 - penaltyPercent;
        }
      }
      
      revenue = Math.round(baseRevenue * lateModifier);
      profit = revenue - mission.totalCost;
      
      if (lateModifier < 1.0) {
        const penaltyPercent = ((1 - lateModifier) * 100).toFixed(1);
        newsMessage = `Mission to ${mission.asteroidName} complete! Earned ${formatCurrency(revenue)} (${penaltyPercent}% late penalty)`;
      } else {
        newsMessage = `Mission to ${mission.asteroidName} complete! Earned ${formatCurrency(revenue)}`;
      }
    } else {
      // No contract, no storage - sell at market (fallback)
      const pricePerTon = 50000;
      revenue = Math.round(resourcesGained * pricePerTon);
      profit = revenue - mission.totalCost;
      newsMessage = `Mission to ${mission.asteroidName} complete! Earned ${formatCurrency(revenue)}`;
    }

    // Add to flight log (keep last 3)
    const logEntry: FlightLogEntry = {
      asteroidName: mission.asteroidName,
      resourceType: resourceTypeForLog,
      tons: resourcesGained,
      profit: profit,
      completedTime: this.gameState.data.gameTime,
    };
    const newLog = [...this.gameState.data.flightLog, logEntry].slice(-3);
    const completedMissionUpdate: Partial<Mission> = {
      resourcesGained,
      revenue,
      resourceType: resourceTypeForLog,
      completionProcessed: true,
    };
    const activeMissions = this.gameState.data.activeMissions.map(activeMission =>
      activeMission.id === mission.id
        ? { ...activeMission, ...completedMissionUpdate }
        : activeMission
    );

    const stateUpdate: Partial<GameStateData> = {
      activeMissions,
      missionsCompleted: this.gameState.data.missionsCompleted + 1,
      flightLog: newLog,
    };
    if (revenue > 0) {
      stateUpdate.balance = this.gameState.data.balance + revenue;
    }
    if (updatedStorage) {
      stateUpdate.storage = updatedStorage;
    }
    
    // Increment security relationship if security was hired
    if (mission.securityId) {
      const currentRel = this.gameState.data.securityRelationships[mission.securityId] || 0;
      if (currentRel < 10) {
        stateUpdate.securityRelationships = {
          ...this.gameState.data.securityRelationships,
          [mission.securityId]: Math.min(10, currentRel + 1),
        };
      }
    }

    this.gameState.update(stateUpdate);

    // Update dependent displays
    this.renderFlightLog();
    if (updatedStorage) {
      this.updateStoragePanel();
    }

    // Show news
    this.addNewsItem(newsMessage, 'important');
    return true;
  }

  private updateAsteroidCount(): void {
    const countEl = document.getElementById('asteroid-count');
    if (countEl && this.solarSystem) {
      countEl.textContent = `${this.solarSystem.getAsteroidCount()} asteroids loaded`;
    }
  }

  private updateTimeDisplay(index: number): void {
    const display = document.getElementById('time-display');
    if (!display) return;
    display.textContent = TIME_SCALE_NAMES[index];
  }

  private updateGameDate(): void {
    const dateEl = document.getElementById('game-date');
    if (!dateEl) return;

    // Game starts January 1, 2032
    const startDate = new Date(2032, 0, 1); // January 1, 2032
    const gameDays = this.gameState.data.gameTime;
    
    // Add game days to start date
    const currentDate = new Date(startDate.getTime() + gameDays * 24 * 60 * 60 * 1000);
    
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[currentDate.getMonth()];
    const year = currentDate.getFullYear();
    
    dateEl.textContent = `${month} ${year}`;
  }

  private onCameraFocusChanged(focusData: any): void {
    const breadcrumb = document.getElementById('breadcrumb');
    if (!breadcrumb) return;

    if (focusData) {
      // Show breadcrumb
      breadcrumb.style.display = 'block';
    } else {
      // Hide breadcrumb
      breadcrumb.style.display = 'none';
    }
  }

  private addNewsItem(text: string, type: NewsType): void {
    this.tickerManager.addItem(text, type);
  }

  /**
   * Periodic news generation based on game time
   * Checks cooldowns and generates appropriate news items
   */
  private updateNews(): void {
    const currentTime = this.gameState.data.gameTime;
    
    // Only check news every ~0.1 game days to avoid performance issues
    if (currentTime - this.lastNewsCheck < 0.1) return;
    this.lastNewsCheck = currentTime;
    
    // Check for easter eggs
    this.checkEasterEggs();
    
    // Generate competitor news (every ~5 min real time = ~3.5 game days at max speed)
    if (this.newsSystem.canShowNews('competitor', currentTime)) {
      const availableAsteroids = Array.from(this.asteroidData.keys())
        .filter(id => !this.gameState.data.minedAsteroids.includes(id))
        .filter(id => !this.newsSystem.isAsteroidBlocked(id));
      
      if (availableAsteroids.length > 0) {
        const result = this.newsSystem.generateCompetitorNews(availableAsteroids);
        if (result) {
          // Replace asteroid ID with actual name
          let text = result.text;
          if (result.asteroidId) {
            const asteroid = this.asteroidData.get(result.asteroidId);
            if (asteroid) {
              text = text.replace(result.asteroidId, asteroid.name);
            }
          }
          
          this.addNewsItem(text, 'market'); // Competitor uses 'market' style (blue)
          this.newsSystem.recordNewsShown('competitor', currentTime);
          
          // If asteroid was blocked, we might want to update UI
          if (result.blocksAsteroid && result.asteroidId) {
            // Asteroid is now unavailable for player
            // This is tracked in newsSystem.isAsteroidBlocked()
          }
        }
      }
    }
    
    // Generate educational news
    if (this.newsSystem.canShowNews('educational', currentTime)) {
      const news = this.newsSystem.getEducationalNews();
      if (news) {
        this.addNewsItem(news, 'flavor'); // Educational uses 'flavor' style (gray)
        this.newsSystem.recordNewsShown('educational', currentTime);
      }
    }
    
    // Generate flavor news (less frequent than educational)
    if (this.newsSystem.canShowNews('flavor', currentTime)) {
      const news = this.newsSystem.getFlavorNews();
      if (news) {
        this.addNewsItem(news, 'flavor');
        this.newsSystem.recordNewsShown('flavor', currentTime);
      }
    }
  }

  /**
   * Check for easter egg conditions
   */
  private checkEasterEggs(): void {
    // Calculate current game date
    const startDate = new Date(2032, 0, 1); // January 1, 2032
    const currentDate = new Date(startDate.getTime() + this.gameState.data.gameTime * 24 * 60 * 60 * 1000);
    const month = currentDate.getMonth() + 1; // 1-12
    const day = currentDate.getDate();
    const missions = this.gameState.data.missionsCompleted;
    const balance = this.gameState.data.balance;
    
    // Determine balance tier for change detection
    const balanceTier = balance >= 1_000_000_000_000 ? 'trillion' 
      : balance >= 1_000_000_000 ? 'billion' 
      : balance <= 0 ? 'bankrupt' 
      : 'normal';
    
    // Check if state has changed (for isFirstCheck)
    const currentState = { month, day, missions, balanceTier };
    const isFirstCheck = !this.lastEasterEggState 
      || this.lastEasterEggState.month !== month 
      || this.lastEasterEggState.day !== day
      || this.lastEasterEggState.missions !== missions
      || this.lastEasterEggState.balanceTier !== balanceTier;
    
    this.lastEasterEggState = currentState;
    
    const easterEggState: EasterEggState = {
      gameMonth: month,
      gameDay: day,
      missionsCompleted: missions,
      balance,
      isFirstCheck,
    };
    
    const triggered = this.newsSystem.checkEasterEggs(easterEggState);
    triggered.forEach(item => {
      this.addNewsItem(item.text, item.type as 'critical' | 'important' | 'market' | 'flavor');
    });
  }

  /**
   * Check if an asteroid is available (not mined and not blocked by competitors)
   */
  private isAsteroidAvailable(asteroidId: string): boolean {
    return !this.gameState.data.minedAsteroids.includes(asteroidId) 
      && !this.newsSystem.isAsteroidBlocked(asteroidId);
  }

  private gameLoop(currentTime: number): void {
    if (!this.isRunning) return;

    const deltaTime = currentTime - this.lastTime;
    this.lastTime = currentTime;

    // Update ticker scroll (runs regardless of game state)
    this.tickerManager.update(currentTime);

    // Update solar system with time scale
    if (this.solarSystem) {
      const timeScale = this.gameState.data.timeScale;
      this.solarSystem.update(deltaTime, timeScale, currentTime);
      this.solarSystem.render();

      // Update game time (convert ms delta to days based on time scale)
      const daysPassed = (deltaTime / 1000) * timeScale;
      this.gameState.update({ gameTime: this.gameState.data.gameTime + daysPassed });

      // Update game date display
      this.updateGameDate();

      // Update active missions
      this.updateMissions();
      
      // Generate periodic news items
      this.updateNews();
      
      // Update asteroid visibility based on camera position (throttled)
      this.updateAsteroidVisibility(currentTime);
    }

    requestAnimationFrame(this.gameLoop.bind(this));
  }

  /**
   * Update which asteroids are visible based on camera position
   * Keeps TARGET_VISIBLE_ASTEROIDS closest to camera visible
   */
  private updateAsteroidVisibility(currentTime: number): void {
    // Throttle updates
    if (currentTime - this.lastVisibilityUpdate < VISIBILITY_UPDATE_INTERVAL_MS) {
      return;
    }
    this.lastVisibilityUpdate = currentTime;

    if (!this.solarSystem) return;

    const cameraInfo = this.solarSystem.getCameraInfo();
    const allAsteroids = staticAsteroidData as AsteroidInfo[];
    
    // Calculate distance from camera target (in AU) for each asteroid in our dataset
    // Use semi-major axis as proxy for position
    const targetAU = cameraInfo.targetAU || 1.5; // Default to asteroid belt if at origin
    
    // Get asteroids with active missions - these always stay visible
    const activeMissionAsteroidIds = new Set(
      this.gameState.data.activeMissions.map(m => m.asteroidId)
    );
    
    const scored = allAsteroids
      .filter(a => !this.gameState.data.minedAsteroids.includes(a.id)) // Exclude mined
      .map(asteroid => ({
        asteroid,
        // Score based on distance from camera target AU
        distance: Math.abs(asteroid.semiMajorAxis - targetAU),
      }))
      .sort((a, b) => a.distance - b.distance);

    // Get the IDs that should be visible: active missions + closest N asteroids
    const shouldBeVisible = new Set<string>(activeMissionAsteroidIds);
    
    // Fill remaining slots with closest asteroids
    const slotsForDynamic = TARGET_VISIBLE_ASTEROIDS - activeMissionAsteroidIds.size;
    let added = 0;
    for (const { asteroid } of scored) {
      if (added >= slotsForDynamic) break;
      if (!shouldBeVisible.has(asteroid.id)) {
        shouldBeVisible.add(asteroid.id);
        added++;
      }
    }

    // Get currently rendered asteroids
    const currentlyRendered = new Set<string>();
    this.asteroidData.forEach((_, id) => {
      if (this.solarSystem!.hasAsteroid(id)) {
        currentlyRendered.add(id);
      }
    });

    // Remove asteroids that shouldn't be visible anymore
    for (const id of currentlyRendered) {
      if (!shouldBeVisible.has(id)) {
        this.solarSystem.removeAsteroid(id);
        this.asteroidData.delete(id);
      }
    }

    // Add asteroids that should be visible but aren't
    for (const id of shouldBeVisible) {
      if (!currentlyRendered.has(id)) {
        const asteroid = allAsteroids.find(a => a.id === id);
        if (asteroid) {
          // Add to data store
          this.asteroidData.set(asteroid.id, asteroid);
          
          // Add to scene
          const size = asteroid.diameter ? Math.max(0.5, asteroid.diameter / 20) : 1;
          const type = (asteroid.taxonomicClass as 'C' | 'S' | 'M') || 'S';
          this.solarSystem.addAsteroid(asteroid.id, asteroid.name, asteroid.semiMajorAxis, size, type);
        }
      }
    }

    // Ensure active mission asteroid labels are always visible
    for (const id of activeMissionAsteroidIds) {
      this.solarSystem.setLabelVisible(id, true);
    }

    // Update asteroid count if it changed
    this.updateAsteroidCount();
  }

  public stop(): void {
    this.isRunning = false;
    if (this.solarSystem) {
      this.solarSystem.dispose();
    }
  }
}

// Initialize game when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new Game());
} else {
  new Game();
}

// Expose for debugging
(window as any).game = Game;
